import React, { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { jsPDF } from "jspdf";
import "@/App.css";
import { 
  Sparkles, ShieldCheck, Droplets, Sun, Leaf, 
  HeartHandshake, MoonStar, Flame, Upload, Trash2, 
  FileText, Mail, Activity, Wind, ChevronRight, ChevronLeft,
  Check, Star, Heart, Camera, Shield, Zap, ScanFace,
  Share2, Download, Instagram, MessageCircle, ExternalLink,
  Stethoscope, ShoppingBag, Palette, ArrowLeft, CheckCircle2, AlertCircle,
  Video, X, RotateCcw, Image, CircleDot, Focus
} from "lucide-react";
import { Progress } from "@/components/ui/progress";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const WHATSAPP_NUMBER = "919821068017";

// Scroll to top helper
function scrollToTopOfPage() {
  window.scrollTo({ top: 0, behavior: "smooth" });
}

// ============ AURA KIT PRODUCT MAPPING (Complete 8-Aura Source - Final Mapping) ============
// All 8 auras configured from uploaded full mapping sheet
const AURA_KIT_PRODUCTS = {
  purple: {
    kitName: "Repair My Aura Kit",
    concerns: ["Stress-aging", "tired aging skin", "fine lines", "wrinkles", "loss of firmness", "under-eye fine lines", "premium repair", "night renewal"],
    hairConcerns: [], // No hair concerns for Purple
    skinProducts: [
      "Aura C-Glow Brightening Serum - Night",
      "Pink-Gold Aura Renewal Serum - Morning",
      "Face Glow Resurfacing Night Cream / Kumkumadi Night Cream",
      "Pink Aura Collagen Wrapping Mask"
    ],
    hairProducts: [], // No hair products for Purple
    strategicNotes: "Premium repair kit. Use morning/night guidance."
  },
  indigo: {
    kitName: "Correct & Strengthen My Aura Kit",
    concerns: ["Deep uneven tone", "stubborn pigmentation", "dark spots", "pigmentation around mouth", "under-eye dullness", "deeper repair"],
    hairConcerns: ["Weak hair", "deeper repair", "root weakness", "hair fall concern", "dull tired scalp"],
    skinProducts: [
      "Aura C-Glow Brightening Serum",
      "Pink-Gold Aura Renewal Serum",
      "Face Glow Resurfacing Night Cream",
      "Aura Veil Day Moisturizer",
      "Aura Clarity Dew Face Mist"
    ],
    hairProducts: [
      "KeshAmrit Root Milk Scalp Concentrate",
      "Aura Scalp Reset Cleanser"
    ],
    strategicNotes: "Pigmentation correction and strengthening focus."
  },
  blue: {
    kitName: "Calm & Brighten My Aura Kit",
    concerns: ["Pigmentation", "tanning", "sun damage", "uneven skin tone", "dullness", "calm brightening", "hydration", "dehydrated skin"],
    hairConcerns: ["Dry hair lengths", "dehydration", "dull hair", "sun/pollution dryness", "lack of shine"],
    skinProducts: [
      "Aura Melt Oil Cleanser",
      "Pink-Gold Aura Renewal Serum",
      "Aura Veil Day Moisturizer",
      "AuraBeam Golden Strobe Cream",
      "Aura Clarity Dew Face Mist"
    ],
    hairProducts: [
      "Aura Glass Veil Hair Mist",
      "Aura Frizz Melt Hair Smoothie"
    ],
    strategicNotes: "Good glow + tan/sunscreen removal positioning."
  },
  green: {
    kitName: "Balance My Aura Kit",
    concerns: ["Balance", "pore reset", "open pores", "mild blackheads", "calming", "redness-prone/reactive skin", "skin harmony"],
    hairConcerns: ["Scalp harmony", "frizz control", "balance", "mild buildup", "oily/scalp imbalance", "reactive scalp feel"],
    skinProducts: [
      "Aura Pore Reset Rose Glow Mask",
      "Aura Veil Day Moisturizer",
      "Aura pH Reactive Lip Stain Oil",
      "Aura Clarity Dew Face Mist"
    ],
    hairProducts: [
      "Aura Scalp Reset Cleanser",
      "Aura Frizz Melt Hair Smoothie"
    ],
    strategicNotes: "Calming and balance focus for reactive skin/scalp."
  },
  yellow: {
    kitName: "Brighten My Aura Kit",
    concerns: ["Dullness", "lack of glow", "freshness", "brightness", "tired-looking skin", "day glow", "sun protection support"],
    hairConcerns: [], // No hair concerns for Yellow
    skinProducts: [
      "Aura C-Glow Brightening Serum",
      "Aura Veil Day Moisturizer",
      "AuraBeam Golden Strobe Cream",
      "AuraHalo Illuminating Dusting Powder",
      "Aura Clarity Dew Face Mist"
    ],
    hairProducts: [], // No hair products for Yellow
    strategicNotes: "Strong glow/day kit."
  },
  orange: {
    kitName: "Energize My Aura Kit",
    concerns: ["Acne", "pimples", "acne marks", "texture", "clogged pores", "blackheads", "whiteheads", "exfoliation", "uneven surface", "instant energy"],
    hairConcerns: [], // No hair concerns for Orange
    skinProducts: [
      "Aura Melt Oil Cleanser",
      "Aura Clear Spot Corrector",
      "Aura Face Glow Resurfacing Night Cream",
      "Pink-Gold Aura Renewal Serum",
      "Aura Clarity Dew Face Mist"
    ],
    hairProducts: [], // No hair products for Orange
    strategicNotes: "Oil balance and acne/texture focus."
  },
  pink: {
    kitName: "Love My Aura Kit",
    concerns: ["Softness", "cheeks", "feminine glow", "beauty enhancement", "nourishment", "dark pigmented lips", "uneven lip tone", "dry/chapped lips"],
    hairConcerns: [], // No hair concerns for Pink
    skinProducts: [
      "Aura Melt Oil Cleanser",
      "AuraGlow Blush",
      "Lip Product",
      "AuraBeam Golden Strobe Cream",
      "AuraHalo Illuminating Dusting Powder"
    ],
    hairProducts: [], // No hair products for Pink
    strategicNotes: "Balanced aura: enhance glow with skin-loving makeup."
  },
  red: {
    kitName: "Restore & Power My Aura Kit",
    concerns: ["Barrier damage", "dryness", "dehydration", "sensitivity", "redness", "rough sensitive skin"],
    hairConcerns: ["Hair fall", "weak roots", "deep nourishment"],
    skinProducts: [
      "Pink-Gold Aura Renewal Serum",
      "Aura Veil Day Moisturizer",
      "Aura Face Glow Resurfacing Night Cream",
      "Pink Aura Collagen Wrapping Mask"
    ],
    hairProducts: [
      "Aura Scalp Reset Cleanser",
      "Aura Frizz Melt Hair Smoothie",
      "KeshAmrit Root Milk Scalp Concentrate"
    ],
    strategicNotes: "Barrier repair and root strength focus."
  }
};

// ============ AURA COPY (For Explanation & Results) ============
const AURA_COPY = {
  purple: {
    label: "Purple Aura",
    concernTitle: "Age Defense, Repair & Renewal",
    intro: "Represents repair, firmness, fine lines, resilience, and renewal support.",
    resultMeaning: "When Purple Aura appears in your result, it means this is a priority care area that needs stronger repair, renewal, and targeted support right now.",
    colorClass: "from-violet-400 to-fuchsia-400",
    // Rich details
    meaning: "Renewal, age defense, repair, fine lines, resilience",
    whatItSignals: "Skin needs renewal and stronger restorative support.",
    productDirection: "Night serums, repair creams, renewal oils, peptide-rich care.",
    accentClass: "text-violet-600",
    softBgClass: "bg-violet-50",
  },
  indigo: {
    label: "Indigo Aura",
    concernTitle: "Clarity, Pigmentation & Precision Care",
    intro: "Represents pigmentation, uneven tone, marks, and clarity of skin texture.",
    resultMeaning: "When Indigo Aura appears in your result, it means pigmentation, uneven tone, or marks need more focused care right now.",
    colorClass: "from-indigo-500 to-blue-500",
    meaning: "Pigmentation, uneven tone, marks, clarity",
    whatItSignals: "Skin needs more targeted tone correction and clarity.",
    productDirection: "Brightening serums, corrective gels, spot-care, radiance support.",
    accentClass: "text-indigo-600",
    softBgClass: "bg-indigo-50",
  },
  blue: {
    label: "Blue Aura",
    concernTitle: "Barrier, Calm & Hydration",
    intro: "Represents hydration, barrier strength, sensitivity comfort, and cooling support.",
    resultMeaning: "When Blue Aura appears in your result, it means your skin or scalp needs more hydration, barrier support, and soothing care.",
    colorClass: "from-sky-400 to-blue-500",
    meaning: "Barrier, hydration, soothing, sensitivity comfort",
    whatItSignals: "Skin or scalp needs hydration and calming reinforcement.",
    productDirection: "Moisturizers, barrier creams, soothing gels, comforting mists.",
    accentClass: "text-sky-600",
    softBgClass: "bg-sky-50",
  },
  green: {
    label: "Green Aura",
    concernTitle: "Recovery, Balance & Calm",
    intro: "Represents calming, balancing, recovery, and healing support.",
    resultMeaning: "When Green Aura appears in your result, it means irritation, imbalance, or stressed skin/scalp needs calming and recovery support.",
    colorClass: "from-emerald-400 to-green-500",
    meaning: "Balance, recovery, calming, reset",
    whatItSignals: "Skin or scalp is stressed, imbalanced, or irritated.",
    productDirection: "Calming creams, recovery gels, gentle scalp care, balancing treatments.",
    accentClass: "text-emerald-600",
    softBgClass: "bg-emerald-50",
  },
  yellow: {
    label: "Yellow Aura",
    concernTitle: "Glow, Brightness & Radiance",
    intro: "Represents dullness recovery, brightness, glow revival, and healthy radiance.",
    resultMeaning: "When Yellow Aura appears in your result, it means dullness and lack of glow need more targeted brightening and radiance support.",
    colorClass: "from-yellow-300 to-amber-400",
    meaning: "Glow, radiance, brightness, vitality",
    whatItSignals: "Skin looks dull and needs visible glow support.",
    productDirection: "Glow serums, radiance creams, strobe products, illuminating care.",
    accentClass: "text-amber-600",
    softBgClass: "bg-amber-50",
  },
  orange: {
    label: "Orange Aura",
    concernTitle: "Oil Balance, Breakout & Reset Care",
    intro: "Represents oil balance, acne-prone skin, congestion, and reset support.",
    resultMeaning: "When Orange Aura appears in your result, it means congestion, oil imbalance, or breakout-prone areas need better control and care.",
    colorClass: "from-orange-400 to-pink-500",
    meaning: "Oil balance, congestion, breakout reset",
    whatItSignals: "Skin needs clearer, more balanced care.",
    productDirection: "Acne support, oil-balancing care, detox gels, reset routines.",
    accentClass: "text-orange-600",
    softBgClass: "bg-orange-50",
  },
  pink: {
    label: "Pink Aura",
    concernTitle: "Nourishment, Softness & Comfort",
    intro: "Represents nourishment, softness, gentle care, comfort, and skin feel improvement.",
    resultMeaning: "When Pink Aura appears in your result, it means your skin or hair needs more nourishment, softness, and comfort support.",
    colorClass: "from-pink-300 to-rose-400",
    meaning: "Softness, nourishment, comfort, gentle care",
    whatItSignals: "Skin or lips need nourishing, softening support.",
    productDirection: "Lip care, comfort creams, nourishing balms, gentle everyday care.",
    accentClass: "text-pink-600",
    softBgClass: "bg-pink-50",
  },
  red: {
    label: "Red Aura",
    concernTitle: "Strength, Roots & Resilience",
    intro: "Represents root support, scalp strength, resilience, and strengthening care.",
    resultMeaning: "When Red Aura appears in your result, it means strength, scalp support, or hair-root resilience needs more focused improvement.",
    colorClass: "from-rose-500 to-red-500",
    meaning: "Strength, roots, resilience, scalp support",
    whatItSignals: "Hair and scalp need strengthening and root support.",
    productDirection: "Hair oil, root tonic, scalp serum, strengthening routines.",
    accentClass: "text-rose-600",
    softBgClass: "bg-rose-50",
  },
};

// ============ AURA SYSTEM ============
const AURA_META = {
  purple: {
    label: "Purple Aura",
    theme: "Age Defense & Renewal",
    icon: Sparkles,
    color: "#9B7ED9",
    gradient: "linear-gradient(135deg, #9B7ED9 0%, #C4A8E8 100%)",
    summary: "Focused on firmness, repair, fine lines, resilience, and renewal support.",
    skinProducts: [
      { name: "Bakuchiol Night Serum", shortDescription: "Plant-based retinol alternative", category: "customize" },
      { name: "Peptide Repair Cream", shortDescription: "Firming peptide moisturizer", category: "website", productUrl: "#" },
      { name: "Age Defense Face Oil", shortDescription: "Antioxidant-rich facial oil", category: "customize" }
    ],
    hairProducts: [
      { name: "Scalp Renewal Serum", shortDescription: "Anti-aging scalp treatment", category: "customize" },
      { name: "Strength Ritual Hair Oil", shortDescription: "Strengthening hair oil", category: "website", productUrl: "#" },
      { name: "Root Recovery Tonic", shortDescription: "Root-strengthening tonic", category: "customize" }
    ],
    ritualFocus: ["Repair", "Strengthen", "Renew"],
  },
  indigo: {
    label: "Indigo Aura",
    theme: "Clarity & Precision Care",
    icon: ScanFace,
    color: "#6366F1",
    gradient: "linear-gradient(135deg, #6366F1 0%, #A5B4FC 100%)",
    summary: "Targets pigmentation, uneven tone, marks, and clarity of skin texture.",
    skinProducts: [
      { name: "Clarity Correct Serum", shortDescription: "Pigmentation-targeting serum", category: "customize" },
      { name: "Bright Balance Gel Cream", shortDescription: "Tone-evening moisturizer", category: "website", productUrl: "#" },
      { name: "Spot-Fade Ritual", shortDescription: "Dark spot treatment", category: "customize" }
    ],
    hairProducts: [
      { name: "Scalp Clarity Tonic", shortDescription: "Clarifying scalp treatment", category: "website", productUrl: "#" },
      { name: "Build-Up Reset Rinse", shortDescription: "Deep cleansing rinse", category: "website", productUrl: "#" },
      { name: "Purity Root Mist", shortDescription: "Refreshing root spray", category: "customize" }
    ],
    ritualFocus: ["Correct", "Refine", "Clarify"],
  },
  blue: {
    label: "Blue Aura",
    theme: "Barrier Calm & Deep Hydration",
    icon: Droplets,
    color: "#60A5FA",
    gradient: "linear-gradient(135deg, #60A5FA 0%, #93C5FD 100%)",
    summary: "Best for dehydration, sensitivity, weakened barrier, and comfort restoration.",
    skinProducts: [
      { name: "Barrier Restore Serum", shortDescription: "Ceramide-rich barrier serum", category: "website", productUrl: "#" },
      { name: "Calm Dew Cream", shortDescription: "Soothing hydrating cream", category: "website", productUrl: "#" },
      { name: "Hydra Veil Mist", shortDescription: "Hydrating face mist", category: "customize" }
    ],
    hairProducts: [
      { name: "Hydra Barrier Scalp Serum", shortDescription: "Scalp hydration serum", category: "customize" },
      { name: "Moisture Seal Conditioner", shortDescription: "Deep moisture conditioner", category: "website", productUrl: "#" },
      { name: "Comfort Hydration Mask", shortDescription: "Intensive hair mask", category: "customize" }
    ],
    ritualFocus: ["Hydrate", "Soothe", "Seal"],
  },
  green: {
    label: "Green Aura",
    theme: "Calm & Soothe",
    icon: Leaf,
    color: "#6EE7B7",
    gradient: "linear-gradient(135deg, #6EE7B7 0%, #A7F3D0 100%)",
    summary: "Supports reactive, redness-prone, stressed, and easily irritated skin or scalp.",
    skinProducts: [
      { name: "Soothe Recovery Gel", shortDescription: "Calming aloe-based gel", category: "website", productUrl: "#" },
      { name: "Green Calm Mask", shortDescription: "Anti-redness face mask", category: "customize" },
      { name: "Sensitive Skin Elixir", shortDescription: "Gentle skin oil", category: "customize" }
    ],
    hairProducts: [
      { name: "Soothe Scalp Elixir", shortDescription: "Scalp calming treatment", category: "customize" },
      { name: "Calm Root Therapy", shortDescription: "Root soothing serum", category: "customize" },
      { name: "Comfort Wash Ritual", shortDescription: "Gentle cleansing set", category: "website", productUrl: "#" }
    ],
    ritualFocus: ["Calm", "Rebalance", "Protect"],
  },
  yellow: {
    label: "Yellow Aura",
    theme: "Glow Recovery",
    icon: Sun,
    color: "#FCD34D",
    gradient: "linear-gradient(135deg, #FCD34D 0%, #FDE68A 100%)",
    summary: "Helps revive dull, tired-looking skin and restore visible radiance and freshness.",
    skinProducts: [
      { name: "Glow Recovery Serum", shortDescription: "Vitamin C brightening serum", category: "website", productUrl: "#" },
      { name: "Radiance Polish", shortDescription: "Gentle exfoliating scrub", category: "website", productUrl: "#" },
      { name: "Glass Skin Shield", shortDescription: "Luminous finishing serum", category: "customize" }
    ],
    hairProducts: [
      { name: "Shine Boost Mist", shortDescription: "Instant shine spray", category: "website", productUrl: "#" },
      { name: "Gloss Repair Leave-In", shortDescription: "Glossing leave-in treatment", category: "customize" },
      { name: "Lustre Finish Serum", shortDescription: "High-shine hair serum", category: "customize" }
    ],
    ritualFocus: ["Revive", "Illuminate", "Refresh"],
  },
  orange: {
    label: "Orange Aura",
    theme: "Acne Balance & Oil Harmony",
    icon: Flame,
    color: "#FB923C",
    gradient: "linear-gradient(135deg, #FB923C 0%, #FDBA74 100%)",
    summary: "Ideal for oily, congestion-prone, breakout-prone skin and unbalanced scalp conditions.",
    skinProducts: [
      { name: "Balance Control Serum", shortDescription: "Oil-regulating serum", category: "website", productUrl: "#" },
      { name: "Clarifying Gel Moisturizer", shortDescription: "Lightweight gel cream", category: "website", productUrl: "#" },
      { name: "Pore Reset Mask", shortDescription: "Deep cleansing clay mask", category: "customize" }
    ],
    hairProducts: [
      { name: "Scalp Balance Tonic", shortDescription: "Oil-control scalp tonic", category: "customize" },
      { name: "Purifying Hair Mist", shortDescription: "Refreshing scalp spray", category: "website", productUrl: "#" },
      { name: "Deep Reset Wash", shortDescription: "Clarifying shampoo", category: "website", productUrl: "#" }
    ],
    ritualFocus: ["Balance", "Purify", "Reset"],
  },
  pink: {
    label: "Pink Aura",
    theme: "Soft Glow & Nourishment",
    icon: HeartHandshake,
    color: "#F472B6",
    gradient: "linear-gradient(135deg, #F472B6 0%, #FBCFE8 100%)",
    summary: "Supports softness, nourishment, comfort, suppleness, and healthy glow.",
    skinProducts: [
      { name: "Soft Glow Cream", shortDescription: "Deeply nourishing moisturizer", category: "website", productUrl: "#" },
      { name: "Nourish Dew Oil", shortDescription: "Lightweight facial oil", category: "customize" },
      { name: "Comfort Tint Balm", shortDescription: "Soothing lip & cheek tint", category: "website", productUrl: "#" }
    ],
    hairProducts: [
      { name: "Soft Silk Hair Serum", shortDescription: "Smoothing hair serum", category: "website", productUrl: "#" },
      { name: "Nourish Ends Oil", shortDescription: "Split-end repair oil", category: "customize" },
      { name: "Velvet Gloss Milk", shortDescription: "Shine-enhancing milk", category: "customize" }
    ],
    ritualFocus: ["Nourish", "Soften", "Glow"],
  },
  red: {
    label: "Red Aura",
    theme: "Strength, Roots & Resilience",
    icon: Flame,
    color: "#EF4444",
    gradient: "linear-gradient(135deg, #EF4444 0%, #FCA5A5 100%)",
    summary: "Supports root strength, scalp resilience, and targeted strengthening care.",
    skinProducts: [
      { name: "Strength Restore Serum", shortDescription: "Firming peptide serum", category: "customize" },
      { name: "Resilience Barrier Cream", shortDescription: "Protective day cream", category: "website", productUrl: "#" },
      { name: "Power Reset Mask", shortDescription: "Weekly strengthening mask", category: "customize" }
    ],
    hairProducts: [
      { name: "Root Strength Tonic", shortDescription: "Scalp-strengthening treatment", category: "customize" },
      { name: "Hair Resilience Serum", shortDescription: "Anti-breakage serum", category: "website", productUrl: "#" },
      { name: "Deep Strength Mask", shortDescription: "Weekly intensive mask", category: "customize" }
    ],
    ritualFocus: ["Strengthen", "Protect", "Restore"],
  },
  white: {
    label: "White Aura",
    theme: "Clean Reset",
    icon: MoonStar,
    color: "#F8FAFC",
    gradient: "linear-gradient(135deg, #F8FAFC 0%, #E2E8F0 100%)",
    summary: "Minimal reset-focused care for overloaded, confused, or over-treated skin and scalp.",
    skinProducts: [
      { name: "Clean Reset Cleanser", shortDescription: "Gentle purifying wash", category: "website", productUrl: "#" },
      { name: "Essential Barrier Gel", shortDescription: "Minimalist moisturizer", category: "website", productUrl: "#" },
      { name: "Recovery Base Cream", shortDescription: "Simple recovery cream", category: "customize" }
    ],
    hairProducts: [
      { name: "Gentle Reset Cleanser", shortDescription: "Clarifying shampoo", category: "website", productUrl: "#" },
      { name: "Minimal Care Scalp Serum", shortDescription: "Lightweight scalp care", category: "customize" },
      { name: "Quiet Routine Rinse", shortDescription: "Simple conditioning rinse", category: "customize" }
    ],
    ritualFocus: ["Simplify", "Reset", "Recover"],
  },
};

const ENERGY_STATE_META = {
  calm: {
    label: "Calm & Balanced",
    summary: "Your skin and scalp appear relatively steady and balanced right now.",
    icon: Leaf,
    color: "#6EE7B7",
  },
  stressed: {
    label: "Stressed",
    summary: "Signals point to irritation, inflammation, or reactivity that needs calming support.",
    icon: Wind,
    color: "#F472B6",
  },
  fatigued: {
    label: "Fatigued",
    summary: "Dullness, dehydration, and low vitality suggest recovery and glow support are needed.",
    icon: MoonStar,
    color: "#A78BFA",
  },
  overloaded: {
    label: "Overloaded",
    summary: "Congestion, oiliness, or build-up suggest the routine should be simplified and rebalanced.",
    icon: Activity,
    color: "#FB923C",
  },
  repairMode: {
    label: "Repair Mode",
    summary: "Your skin or scalp is asking for restoration, barrier support, and a gentler routine.",
    icon: ShieldCheck,
    color: "#60A5FA",
  },
};

// ============ QUESTIONNAIRE DATA ============
const QUESTIONS = {
  buying_priorities: {
    title: "What matters to you most when buying beauty products?",
    subtitle: "Select your top 3 priorities",
    max: 3,
    options: [
      "Dermatologically safe",
      "Visible results",
      "Clean ingredients",
      "Premium packaging",
      "Value for money",
      "Brand and ingredients transparency",
      "Natural formulations"
    ]
  },
  skin_concerns: {
    title: "Which of the following best describes your current concerns?",
    subtitle: "Select up to 4",
    max: 4,
    options: [
      "Skin gets tanned easily",
      "Early fine lines, wrinkles or sagging",
      "Pigmentation or uneven skin tone",
      "Sensitive or easily irritated skin",
      "Dull, tired-looking complexion",
      "Acne, breakouts or excess oil",
      "Dehydrated, dry or tight-feeling skin",
      "Dry Pigmented Lips"
    ]
  },
  skin_goals: {
    title: "Your main skincare goal is to:",
    subtitle: "Select up to 3",
    max: 3,
    options: [
      "Look firmer and more youthful",
      "Correct pigmentation precisely",
      "Calm and soothe skin",
      "Restore overall balance",
      "Achieve visible glow",
      "Feel fresh and energized",
      "Deeply hydrate and nourish",
      "Strengthen and protect skin"
    ]
  },
  hair_concerns: {
    title: "What is your biggest hair concern?",
    subtitle: "Select up to 3",
    max: 3,
    options: [
      "Hair thinning or lack of volume",
      "Dry, frizzy or rough texture",
      "Sensitive or itchy scalp",
      "Oily scalp",
      "Dull, lifeless hair",
      "Weak roots or breakage",
      "Just want smooth, healthy-looking hair"
    ]
  },
  hair_feel_goals: {
    title: "How do you want your hair to feel?",
    subtitle: "Select up to 3",
    max: 3,
    options: [
      "Silky and luxurious",
      "Strong and resilient",
      "Light and fresh",
      "Soft and hydrated",
      "Balanced and healthy"
    ]
  },
  routine_feeling: {
    title: "Which feeling do you want your beauty routine to give you?",
    subtitle: "Select one",
    max: 1,
    options: [
      "Calm and balanced",
      "Radiant and glowing",
      "Fresh and energized",
      "Deeply nourished",
      "Youthful and lifted",
      "Clear and refined",
      "Don't have any Beauty Routine"
    ]
  },
  texture_preference: {
    title: "What texture do you enjoy the most in skincare?",
    subtitle: "Select up to 3",
    max: 3,
    options: [
      "Rich and indulgent cream",
      "Lightweight gel",
      "Fast-absorbing serum",
      "Soft lotion",
      "Oil-based nourishment",
      "Minimal, barely-there formula"
    ]
  },
  fragrance_preference: {
    title: "Your fragrance preference:",
    subtitle: "Select up to 3",
    max: 3,
    options: [
      "Subtle premium scent",
      "Fresh botanical notes",
      "Completely fragrance-free",
      "Soft floral",
      "Citrus and uplifting",
      "Warm and comforting"
    ]
  },
  makeup_vibe: {
    title: "Your makeup vibe is:",
    subtitle: "Select one",
    max: 1,
    options: [
      "Minimal & pure",
      "Bold & powerful",
      "Glowy & luminous",
      "Flawless & even",
      "Elegant & timeless"
    ]
  },
  makeup_coverage: {
    title: "Makeup Coverage preference:",
    subtitle: "Select one",
    max: 1,
    options: [
      "Natural everyday finish",
      "Medium coverage",
      "Full coverage",
      "Only targeted correction",
      "I hardly wear any make-up"
    ]
  },
  brand_disappointments: {
    title: "What disappoints you most in beauty brands?",
    subtitle: "Select up to 3",
    max: 3,
    options: [
      "Overpromising but not delivering real results",
      "Lack of transparency in ingredients",
      "Too many harsh chemicals",
      "Products not suited for Indian skin/climate",
      "No real solution for pigmentation",
      "Poor customer support or brand interaction",
      "Overpriced without visible difference",
      "Influencer-driven hype with no substance",
      "Complicated routines with too many products"
    ]
  },
  brand_solutions: {
    title: "If one brand could solve 3 major issues for you, what would that be?",
    subtitle: "Select up to 3",
    max: 3,
    options: [
      "Persistent pigmentation / uneven skin tone",
      "Under eye dark circle",
      "Dull, tired-looking skin",
      "Hormonal acne / recurring breakouts",
      "Dark underarms / elbows / knees",
      "Hair fall / thinning hair",
      "Frizz / dry damaged hair",
      "Sensitive or reactive skin",
      "Long-lasting natural makeup for Indian skin tone",
      "Dark pigmented lips"
    ]
  },
  body_skin: {
    title: "How would you describe your body skin?",
    subtitle: "Select up to 3",
    max: 3,
    options: [
      "Very dry",
      "Rough or uneven",
      "Healthy but needs nourishment",
      "Smooth and resilient",
      "Dark elbows, Knees, Neck and Underarms",
      "Cracked Heels/Rough Feet",
      "Rough Hands"
    ]
  },
  early_access: {
    title: "Would you like early access to launches, special pricing & private previews?",
    subtitle: "Select one",
    max: 1,
    options: [
      "Yes, absolutely — I love early access",
      "Maybe, if benefits are clear",
      "Not sure yet",
      "No"
    ]
  }
};

// Scoring maps
const SKIN_CONCERN_SCORES = {
  "Skin gets tanned easily": ["indigo", "yellow"],
  "Early fine lines, wrinkles or sagging": ["purple", "pink"],
  "Pigmentation or uneven skin tone": ["indigo", "yellow"],
  "Sensitive or easily irritated skin": ["green", "blue"],
  "Dull, tired-looking complexion": ["yellow", "pink"],
  "Acne, breakouts or excess oil": ["orange", "green"],
  "Dehydrated, dry or tight-feeling skin": ["blue", "pink"],
  "Dry Pigmented Lips": ["pink", "indigo"],
};

const SKIN_GOAL_SCORES = {
  "Look firmer and more youthful": ["purple", "pink"],
  "Correct pigmentation precisely": ["indigo", "yellow"],
  "Calm and soothe skin": ["green", "blue"],
  "Restore overall balance": ["white", "green"],
  "Achieve visible glow": ["yellow", "pink"],
  "Feel fresh and energized": ["yellow", "white"],
  "Deeply hydrate and nourish": ["blue", "pink"],
  "Strengthen and protect skin": ["blue", "purple"],
};

const HAIR_CONCERN_SCORES = {
  "Hair thinning or lack of volume": ["purple", "indigo"],
  "Dry, frizzy or rough texture": ["blue", "pink"],
  "Sensitive or itchy scalp": ["green", "blue"],
  "Oily scalp": ["orange", "white"],
  "Dull, lifeless hair": ["yellow", "pink"],
  "Weak roots or breakage": ["purple", "green"],
  "Just want smooth, healthy-looking hair": ["pink", "blue"],
};

const HAIR_GOAL_SCORES = {
  "Silky and luxurious": ["pink", "blue"],
  "Strong and resilient": ["purple", "green"],
  "Light and fresh": ["white", "yellow"],
  "Soft and hydrated": ["blue", "pink"],
  "Balanced and healthy": ["green", "white"],
};

const ROUTINE_SCORES = {
  "Calm and balanced": ["green", "white"],
  "Radiant and glowing": ["yellow", "pink"],
  "Fresh and energized": ["yellow", "white"],
  "Deeply nourished": ["pink", "blue"],
  "Youthful and lifted": ["purple", "pink"],
  "Clear and refined": ["indigo", "orange"],
};

// ============ UTILITY FUNCTIONS ============
function toggleInArray(arr, value, max = 3) {
  if (arr.includes(value)) return arr.filter((x) => x !== value);
  if (arr.length >= max) return arr;
  return [...arr, value];
}

function computeEnergyState({ detectedFlags, primary, secondary, skinConcerns, hairConcerns }) {
  const has = (flag) => detectedFlags.includes(flag);

  if (
    has("redness") ||
    has("scalpSensitivity") ||
    skinConcerns.includes("Sensitive or easily irritated skin") ||
    hairConcerns.includes("Sensitive or itchy scalp")
  ) {
    return "stressed";
  }

  if (
    has("acne") ||
    has("oilyscalp") ||
    skinConcerns.includes("Acne, breakouts or excess oil") ||
    hairConcerns.includes("Oily scalp")
  ) {
    return "overloaded";
  }

  if (
    has("dryness") ||
    has("dullness") ||
    primary === "blue" ||
    secondary === "yellow" ||
    skinConcerns.includes("Dull, tired-looking complexion")
  ) {
    return "fatigued";
  }

  if (
    has("lines") ||
    has("hairfall") ||
    skinConcerns.includes("Dehydrated, dry or tight-feeling skin") ||
    hairConcerns.includes("Weak roots or breakage") ||
    primary === "purple"
  ) {
    return "repairMode";
  }

  return "calm";
}

function computeAuraScores(form) {
  const scores = {
    purple: 0, indigo: 0, blue: 0, green: 0,
    yellow: 0, orange: 0, pink: 0, red: 0,
  };

  // Check if this is Digital-only mode with NO images uploaded
  const isDigitalOnlyNoImages = form.analysisMode === "digital" && 
    form.detectedFlags.includes("no_images_uploaded");

  if (isDigitalOnlyNoImages) {
    // Return a special result indicating no valid analysis was possible
    return {
      scores,
      primary: null,
      secondary: null,
      energyState: null,
      percentages: {},
      noValidAnalysis: true,
      message: "Please upload at least one image for Digital Analysis, or try our Questionnaire or Hybrid mode for accurate results."
    };
  }

  const apply = (items, map, weight = 1) => {
    items.forEach((item) => {
      (map[item] || []).forEach((aura, index) => {
        scores[aura] += index === 0 ? 2 * weight : 1 * weight;
      });
    });
  };

  apply(form.skin_concerns, SKIN_CONCERN_SCORES, 2);
  apply(form.skin_goals, SKIN_GOAL_SCORES, 2);
  apply(form.hair_concerns, HAIR_CONCERN_SCORES, 2);
  apply(form.hair_feel_goals, HAIR_GOAL_SCORES, 2);
  apply([form.routine_feeling].filter(Boolean), ROUTINE_SCORES, 1);

  // Digital analysis flags (for Digital-only and Hybrid modes)
  if (form.analysisMode !== "questionnaire") {
    // ============ DIGITAL-ONLY MODE: STRICT WEIGHTED SCORING ============
    // Uses strict weighted scoring based on user's checklist requirements
    // Strong indicator = 4 points, Moderate = 2 points, Mild = 1 point
    
    const flags = form.detectedFlags || [];
    
    // PURPLE AURA: Fine lines, wrinkles, under-eye fine lines, tired aging skin, stress-aged appearance
    if (flags.includes("lines")) scores.purple += 4;           // Strong: fine lines/wrinkles
    if (flags.includes("undereyeLines")) scores.purple += 4;   // Strong: under-eye fine lines
    if (flags.includes("tired")) scores.purple += 3;           // Strong: tired aging/stress-aged look
    if (flags.includes("undereye")) scores.purple += 3;        // Moderate: under-eye area concerns
    if (flags.includes("repairNeed")) scores.purple += 3;      // Strong: loss of firmness/repair need
    
    // INDIGO AURA: Deep pigmentation, stubborn pigmentation, localized dark patches, under-eye dullness
    if (flags.includes("deepPigmentation")) scores.indigo += 4;  // Strong: deep pigmentation patches
    if (flags.includes("stubbornTone")) scores.indigo += 4;      // Strong: stubborn uneven tone
    if (flags.includes("undereyeDarkness")) scores.indigo += 3;  // Strong: under-eye dullness/darkness
    if (flags.includes("patchyTone")) scores.indigo += 2;        // Moderate: localized marks/patchiness
    
    // BLUE AURA: Tanning, diffuse uneven tone, mild pigmentation with dehydration
    if (flags.includes("tanning")) scores.blue += 4;           // Strong: tanning/sun damage appearance
    if (flags.includes("diffuseUnevenTone")) scores.blue += 3; // Strong: widespread uneven tone
    if (flags.includes("dryness")) scores.blue += 3;           // Strong: dehydration/tight dull skin
    if (flags.includes("pigmentation")) scores.blue += 2;      // Moderate: pigmentation + dryness
    if (flags.includes("frizz")) scores.blue += 2;             // Hair: dry/frizzy hair
    if (flags.includes("dullHair")) scores.blue += 2;          // Hair: dull hair
    
    // GREEN AURA: Mild redness, reactive skin, calming need, balance need
    if (flags.includes("redness")) scores.green += 3;          // Strong: mild redness/reactive skin
    if (flags.includes("calmingNeed")) scores.green += 3;      // Strong: balancing/calming need
    if (flags.includes("sensitivity")) scores.green += 2;      // Moderate: sensitivity
    if (flags.includes("poreReset")) scores.green += 2;        // Moderate: visible pore reset need
    if (flags.includes("scalpSensitivity")) scores.green += 2; // Hair: scalp sensitivity
    
    // YELLOW AURA: Dullness, lack of glow, tired-looking skin, brightness deficit
    if (flags.includes("dullness")) scores.yellow += 4;        // Strong: overall dullness
    if (flags.includes("lackOfGlow")) scores.yellow += 4;      // Strong: lack of glow
    if (flags.includes("tiredLooking")) scores.yellow += 2;    // Moderate: tired-looking skin
    if (flags.includes("brightnessDeficit")) scores.yellow += 2; // Moderate: brightness deficit
    
    // ORANGE AURA: Texture, clogged pores, blackheads, congestion, exfoliation need
    if (flags.includes("blackheads")) scores.orange += 4;      // Strong: blackheads
    if (flags.includes("pores")) scores.orange += 4;           // Strong: clogged pores
    if (flags.includes("texture")) scores.orange += 3;         // Strong: rough texture
    if (flags.includes("acne")) scores.orange += 3;            // Strong: acne/breakouts
    if (flags.includes("congestion")) scores.orange += 2;      // Moderate: congestion/exfoliation need
    if (flags.includes("oilyscalp")) scores.orange += 2;       // Hair: oily scalp
    
    // PINK AURA: Lips, cheeks, softness (conservative - should not override stronger concerns)
    if (flags.includes("lipDarkness")) scores.pink += 3;       // Strong: lip darkness/uneven lip tone
    if (flags.includes("dryLips")) scores.pink += 3;           // Strong: dry/chapped lips
    if (flags.includes("softness")) scores.pink += 2;          // Moderate: cheeks/softness/feminine glow
    if (flags.includes("lips")) scores.pink += 2;              // Moderate: general lip concerns
    
    // RED AURA: Barrier damage, visible dryness, flaking, sensitivity + dryness
    if (flags.includes("barrier")) scores.red += 4;            // Strong: barrier damage/compromised look
    if (flags.includes("flaking")) scores.red += 4;            // Strong: visible dryness/flaking
    if (flags.includes("sensitivityDryness")) scores.red += 4; // Strong: sensitivity + dryness combined
    if (flags.includes("weakRoots")) scores.red += 2;          // Moderate: depleted look
    if (flags.includes("hairfall")) scores.red += 2;           // Hair: hair fall/weak roots
  }

  // ============ STRICT STABILITY ENFORCEMENT FOR DIGITAL MODE ============
  // Prevents aura flipping by ensuring clear dominance before selecting primary
  if (form.analysisMode === "digital") {
    // STEP 1: Apply strict precedence rules to enforce hierarchy
    // Red > Orange > Indigo > Purple > Blue > Yellow > Green > Pink
    
    // Red beats Blue, Green, Yellow, Pink when barrier damage is present
    if (scores.red >= 4) {
      scores.blue = Math.min(scores.blue, scores.red - 2);
      scores.green = Math.min(scores.green, scores.red - 3);
      scores.yellow = Math.min(scores.yellow, scores.red - 3);
      scores.pink = Math.min(scores.pink, scores.red - 4);
    }
    
    // Orange beats Green, Yellow, Pink when texture/pores are present
    if (scores.orange >= 4) {
      scores.green = Math.min(scores.green, scores.orange - 2);
      scores.yellow = Math.min(scores.yellow, scores.orange - 2);
      scores.pink = Math.min(scores.pink, scores.orange - 3);
    }
    
    // Indigo beats Blue and Yellow when deep pigmentation is present
    if (scores.indigo >= 4) {
      scores.blue = Math.min(scores.blue, scores.indigo - 2);
      scores.yellow = Math.min(scores.yellow, scores.indigo - 2);
    }
    
    // Purple beats Yellow and Pink when aging signals are present
    if (scores.purple >= 4) {
      scores.yellow = Math.min(scores.yellow, scores.purple - 2);
      scores.pink = Math.min(scores.pink, scores.purple - 3);
    }
    
    // Blue beats Yellow when tanning + dehydration are present
    if (scores.blue >= 4 && scores.yellow > 0) {
      scores.yellow = Math.min(scores.yellow, scores.blue - 2);
    }
    
    // Pink should NEVER beat stronger functional concerns
    const maxFunctionalScore = Math.max(scores.red, scores.orange, scores.indigo, scores.purple, scores.blue, scores.yellow, scores.green);
    if (maxFunctionalScore >= 4) {
      scores.pink = Math.min(scores.pink, maxFunctionalScore - 3);
    }
    
    // Green should lose to Orange if texture/pores are strong
    if (scores.orange >= 6 && scores.green > 0) {
      scores.green = Math.min(scores.green, scores.orange - 3);
    }
    
    // Green should lose to Red if barrier damage is present
    if (scores.red >= 4 && scores.green > 0) {
      scores.green = Math.min(scores.green, scores.red - 3);
    }
  }

  // Sort by score descending with STRICT precedence fallback order:
  // Red > Orange > Indigo > Purple > Blue > Yellow > Green > Pink
  const precedenceOrder = ['red', 'orange', 'indigo', 'purple', 'blue', 'yellow', 'green', 'pink'];
  const sorted = Object.entries(scores).sort((a, b) => {
    if (b[1] !== a[1]) return b[1] - a[1]; // Sort by score descending
    // For ties, use strict precedence order
    return precedenceOrder.indexOf(a[0]) - precedenceOrder.indexOf(b[0]);
  });
  
  const topScore = sorted[0][1];
  const secondScore = sorted[1][1];
  const total = Math.max(sorted.reduce((sum, [, n]) => sum + n, 0), 1);
  
  // ============ CONFIDENCE & STABILITY CHECKS FOR DIGITAL MODE ============
  // Don't force an unreliable aura if confidence is too low
  // Don't allow unstable flipping between close scores
  if (form.analysisMode === "digital") {
    const MINIMUM_CONFIDENCE_SCORE = 4;   // Must have at least 4 points to be confident
    const MINIMUM_DOMINANCE_MARGIN = 2;   // Primary must be at least 2 points ahead for clear dominance
    
    // Check if top score is too low - show retry message
    if (topScore < MINIMUM_CONFIDENCE_SCORE) {
      return {
        scores,
        primary: null,
        secondary: null,
        energyState: null,
        percentages: Object.fromEntries(
          Object.entries(scores).map(([k, v]) => [k, Math.round((v / total) * 100)])
        ),
        noValidAnalysis: true,
        lowConfidence: true,
        message: "We could not confidently detect a strong dominant aura from this image. Please try another image with better lighting, or use Hybrid Analysis for more accurate results."
      };
    }
    
    // Check if top two scores are too close (would cause instability)
    const scoreDifference = topScore - secondScore;
    
    // If scores are too close, use precedence order to break tie definitively
    // This prevents Blue → Green → Orange flipping
    if (scoreDifference < MINIMUM_DOMINANCE_MARGIN && scoreDifference <= 1) {
      // Apply strict precedence - the higher precedence aura wins ties
      const topAura = sorted[0][0];
      const secondAura = sorted[1][0];
      const topPrecedence = precedenceOrder.indexOf(topAura);
      const secondPrecedence = precedenceOrder.indexOf(secondAura);
      
      // If second aura has higher precedence (lower index), it becomes primary
      if (secondPrecedence < topPrecedence) {
        // Swap: use the higher-precedence aura as primary
        const primary = secondAura;
        // ALWAYS show Supporting Aura when second score is meaningful (>= 2)
        const secondary = topScore >= 2 ? topAura : null;
        
        const energyState = computeEnergyState({
          detectedFlags: form.detectedFlags,
          primary,
          secondary: secondary || topAura,
          skinConcerns: form.skin_concerns,
          hairConcerns: form.hair_concerns,
        });

        return {
          scores,
          primary,
          secondary,
          energyState,
          percentages: Object.fromEntries(
            Object.entries(scores).map(([k, v]) => [k, Math.round((v / total) * 100)])
          ),
          noValidAnalysis: false,
          lowConfidence: false,
          confidenceScore: secondScore,
          scoreDifference: Math.abs(scoreDifference),
        };
      }
    }
    
    // Normal case: clear winner
    const primary = sorted[0][0];
    
    // IMPROVED SUPPORTING AURA LOGIC:
    // Show Supporting Aura when the second score is meaningfully relevant
    // Conditions: score >= 2 (not too weak) AND different from primary
    // This ensures we show dual-aura results when appropriate
    const secondary = (secondScore >= 2 && sorted[1][0] !== primary) ? sorted[1][0] : null;
    
    const energyState = computeEnergyState({
      detectedFlags: form.detectedFlags,
      primary,
      secondary: secondary || sorted[1][0],
      skinConcerns: form.skin_concerns,
      hairConcerns: form.hair_concerns,
    });

    return {
      scores,
      primary,
      secondary,
      energyState,
      percentages: Object.fromEntries(
        Object.entries(scores).map(([k, v]) => [k, Math.round((v / total) * 100)])
      ),
      noValidAnalysis: false,
      lowConfidence: false,
      confidenceScore: topScore,
      scoreDifference,
    };
  }
  
  // For Questionnaire and Hybrid modes, use original logic (UNCHANGED)
  const primary = sorted[0][0];
  const secondary = sorted[1][0];
  const energyState = computeEnergyState({
    detectedFlags: form.detectedFlags,
    primary,
    secondary,
    skinConcerns: form.skin_concerns,
    hairConcerns: form.hair_concerns,
  });

  return {
    scores,
    primary,
    secondary,
    energyState,
    percentages: Object.fromEntries(
      Object.entries(scores).map(([k, v]) => [k, Math.round((v / total) * 100)])
    ),
    noValidAnalysis: false,
  };
}

function buildRoutineStrategy(primary, secondary, energyState) {
  const primaryFocus = AURA_META[primary]?.ritualFocus || [];
  const secondaryFocus = AURA_META[secondary]?.ritualFocus || [];
  const energyMap = {
    calm: ["Maintain", "Protect", "Glow"],
    stressed: ["Calm", "Reduce stress", "Protect barrier"],
    fatigued: ["Hydrate", "Revive", "Restore glow"],
    overloaded: ["Simplify", "Reset", "Rebalance"],
    repairMode: ["Repair", "Strengthen", "Recover"],
  };
  return [...primaryFocus, ...secondaryFocus, ...(energyMap[energyState] || [])]
    .filter(Boolean)
    .filter((item, idx, arr) => arr.indexOf(item) === idx)
    .slice(0, 5);
}

function buildTopPriorities(form) {
  const priorityMap = {
    dryness: "Dehydration",
    dullness: "Dullness",
    redness: "Sensitivity / Redness",
    pigmentation: "Pigmentation / Uneven tone",
    acne: "Breakouts / Oil imbalance",
    lines: "Fine lines / Renewal",
    frizz: "Frizz / Moisture loss",
    hairfall: "Weak roots / Hair fall",
    oilyscalp: "Oily scalp",
    scalpSensitivity: "Scalp sensitivity",
  };

  const flags = form.detectedFlags.map((x) => priorityMap[x] || x);
  const questionnaireSignals = [
    ...form.skin_concerns.slice(0, 2),
    ...form.hair_concerns.slice(0, 2),
  ];

  return [...flags, ...questionnaireSignals]
    .filter(Boolean)
    .filter((item, idx, arr) => arr.indexOf(item) === idx)
    .slice(0, 6);
}

// ============ PDF & EMAIL HELPERS ============
function buildPlainTextReport(form, result) {
  console.log("buildPlainTextReport called");
  
  const primaryMeta = AURA_META[result?.primary] || {};
  const secondaryMeta = AURA_META[result?.secondary] || {};
  const energyMeta = ENERGY_STATE_META[result?.energyState] || {};
  const topPriorities = buildTopPriorities(form) || [];
  const routineStrategy = buildRoutineStrategy(result?.primary, result?.secondary, result?.energyState) || [];
  
  const lines = [
    `Divine Aura Analysis Report`,
    `Generated: ${new Date().toLocaleDateString()}`,
    ``,
    form?.name ? `Name: ${form.name}` : "",
    form?.phone ? `Phone: ${form.phone}` : "",
    form?.email ? `Email: ${form.email}` : "",
    ``,
    `--- YOUR AURA PROFILE ---`,
    ``,
    `Primary Aura: ${primaryMeta?.label || result?.primary || 'N/A'}`,
    `Theme: ${primaryMeta?.theme || ''}`,
    `Meaning: ${AURA_COPY[result?.primary]?.resultMeaning || primaryMeta?.summary || ''}`,
    ``,
    `Supporting Aura: ${secondaryMeta?.label || result?.secondary || 'N/A'}`,
    `Theme: ${secondaryMeta?.theme || ''}`,
    `Meaning: ${AURA_COPY[result?.secondary]?.resultMeaning || secondaryMeta?.summary || ''}`,
    ``,
    `Energy State: ${energyMeta?.label || result?.energyState || 'N/A'}`,
    `${energyMeta?.summary || ''}`,
    ``,
    topPriorities.length ? `--- YOUR TOP SKIN & HAIR CONCERNS ---` : '',
    topPriorities.length ? topPriorities.join(', ') : '',
    ``,
    routineStrategy.length ? `--- YOUR CARE STRATEGY ---` : '',
    routineStrategy.length ? routineStrategy.join(', ') : '',
    ``,
    `--- RECOMMENDED SKIN RITUAL ---`,
    ...(primaryMeta?.skinProducts || []).map((p, i) => `${i + 1}. ${typeof p === 'string' ? p : p?.name || 'Product'}`),
    ``,
    `--- RECOMMENDED HAIR RITUAL ---`,
    ...(primaryMeta?.hairProducts || []).map((p, i) => `${i + 1}. ${typeof p === 'string' ? p : p?.name || 'Product'}`),
    ``,
    `---`,
    `Discover your Divine Aura at divineaura.com`
  ];
  
  return lines.filter(Boolean).join('\n');
}

async function downloadReportPdf(form, result) {
  console.log("downloadReportPdf called with:", { form, result });
  
  if (!result || !result.primary) {
    throw new Error("No result data provided");
  }
  
  const doc = new jsPDF();
  const primaryMeta = AURA_META[result.primary] || {};
  const secondaryMeta = AURA_META[result.secondary] || {};
  const energyMeta = ENERGY_STATE_META[result.energyState] || {};
  const topPriorities = buildTopPriorities(form) || [];
  const routineStrategy = buildRoutineStrategy(result.primary, result.secondary, result.energyState) || [];
  
  // Header
  doc.setFont("helvetica", "bold");
  doc.setFontSize(24);
  doc.setTextColor(155, 126, 217);
  doc.text("Divine Aura", 105, 25, { align: "center" });
  
  doc.setFontSize(12);
  doc.setTextColor(100, 100, 100);
  doc.text("Your Personalized Analysis Report", 105, 35, { align: "center" });
  
  // Date
  doc.setFontSize(10);
  doc.setTextColor(150, 150, 150);
  doc.text(`Generated: ${new Date().toLocaleDateString()}`, 105, 42, { align: "center" });
  
  let y = 55;
  
  // User info
  if (form?.name) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.setTextColor(60, 60, 60);
    doc.text(`Prepared for: ${form.name}`, 20, y);
    y += 12;
  }
  
  // Primary Aura
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.setTextColor(155, 126, 217);
  doc.text("Your Primary Aura", 20, y);
  y += 8;
  
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.setTextColor(40, 40, 40);
  doc.text(primaryMeta?.label || result.primary || 'N/A', 20, y);
  y += 7;
  
  doc.setFont("helvetica", "italic");
  doc.setFontSize(11);
  doc.setTextColor(80, 80, 80);
  doc.text(primaryMeta?.theme || '', 20, y);
  y += 8;
  
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(60, 60, 60);
  const primaryMeaning = AURA_COPY[result.primary]?.resultMeaning || primaryMeta?.summary || '';
  const primaryLines = doc.splitTextToSize(primaryMeaning, 170);
  doc.text(primaryLines, 20, y);
  y += primaryLines.length * 5 + 10;
  
  // Secondary Aura
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.setTextColor(155, 126, 217);
  doc.text("Your Supporting Aura", 20, y);
  y += 8;
  
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.setTextColor(40, 40, 40);
  doc.text(secondaryMeta?.label || result.secondary || 'N/A', 20, y);
  y += 7;
  
  doc.setFont("helvetica", "italic");
  doc.setFontSize(10);
  doc.setTextColor(80, 80, 80);
  doc.text(secondaryMeta?.theme || '', 20, y);
  y += 10;
  
  // Energy State
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(155, 126, 217);
  doc.text("Energy State: ", 20, y);
  doc.setTextColor(40, 40, 40);
  doc.text(energyMeta?.label || result.energyState || 'N/A', 60, y);
  y += 12;
  
  // Top Concerns
  if (topPriorities.length > 0) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(155, 126, 217);
    doc.text("Your Top Skin & Hair Concerns", 20, y);
    y += 7;
    
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(60, 60, 60);
    topPriorities.forEach((priority) => {
      doc.text(`• ${priority}`, 25, y);
      y += 5;
    });
    y += 5;
  }
  
  // Care Strategy
  if (routineStrategy.length > 0) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(155, 126, 217);
    doc.text("Your Care Strategy", 20, y);
    y += 7;
    
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(60, 60, 60);
    doc.text(routineStrategy.join(" • "), 25, y);
    y += 12;
  }
  
  // Check if we need a new page
  if (y > 240) {
    doc.addPage();
    y = 20;
  }
  
  // Skin Ritual
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(155, 126, 217);
  doc.text("Recommended Skin Ritual", 20, y);
  y += 7;
  
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(60, 60, 60);
  (primaryMeta?.skinProducts || []).forEach((product, i) => {
    const productName = typeof product === 'string' ? product : product?.name || 'Product';
    doc.text(`${i + 1}. ${productName}`, 25, y);
    y += 5;
  });
  y += 8;
  
  // Hair Ritual
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(155, 126, 217);
  doc.text("Recommended Hair Ritual", 20, y);
  y += 7;
  
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(60, 60, 60);
  (primaryMeta?.hairProducts || []).forEach((product, i) => {
    const productName = typeof product === 'string' ? product : product?.name || 'Product';
    doc.text(`${i + 1}. ${productName}`, 25, y);
    y += 5;
  });
  
  // Footer
  doc.setFontSize(9);
  doc.setTextColor(150, 150, 150);
  doc.text("Discover your Divine Aura at divineaura.com", 105, 285, { align: "center" });
  
  // Generate filename
  const filename = `Divine-Aura-Report-${(form?.name || 'Analysis').replace(/[^a-zA-Z0-9]/g, '-')}.pdf`;
  
  // Create blob and open in new tab (works in iframes/preview windows)
  const pdfBlob = doc.output('blob');
  const blobUrl = URL.createObjectURL(pdfBlob);
  
  // Open in new tab - user can save from there
  const newWindow = window.open(blobUrl, '_blank');
  
  // If popup was blocked, try alternative method
  if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
    // Fallback: create download link
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
  
  // Clean up blob URL after delay
  setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
  
  console.log("PDF opened/downloaded:", filename);
  return filename;
}

function emailMyReport(form, result) {
  console.log("emailMyReport called with:", { form, result });
  
  if (!result || !result.primary) {
    throw new Error("No result data provided");
  }
  
  const reportText = buildPlainTextReport(form, result);
  const subject = encodeURIComponent("My Divine Aura Analysis Report");
  const body = encodeURIComponent(reportText);
  
  // Use window.open for mailto to ensure it works across browsers
  const mailtoUrl = `mailto:${form.email || ''}?subject=${subject}&body=${body}`;
  window.open(mailtoUrl, '_self');
}

function getCustomizeWhatsAppUrl(productName, form, result) {
  const message =
    `Hello Divine Aura,\n\n` +
    `I would like to customize this recommendation:\n` +
    `${productName}\n\n` +
    `My analysis summary:\n` +
    `${buildPlainTextReport(form, result)}\n\n` +
    `Please share details, pricing, and next steps on WhatsApp.`;

  return `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`;
}

// ============ COMPONENTS ============

// Logo Component
const Logo = () => (
  <div className="flex items-center gap-2" data-testid="logo">
    <img 
      src="/divine-aura-logo-cropped.png" 
      alt="Divine Aura" 
      className="h-12 md:h-14 w-auto object-contain"
      style={{ mixBlendMode: 'multiply' }}
    />
  </div>
);

// Aura Introduction Section (shown before questionnaire)
const AuraIntroSection = ({ onContinue }) => {
  const orderedAuras = ["purple", "indigo", "blue", "green", "yellow", "orange", "pink", "red"];
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-3xl mx-auto px-4 py-8 pb-32 md:pb-12"
      data-testid="aura-intro-section"
    >
      <div className="text-center mb-8">
        <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-3">Before You Begin</h2>
        <p className="text-gray-600">Understanding what "Aura-Inspired Skincare" means</p>
      </div>
      
      <div className="divine-card mb-6">
        <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-purple-500" />
          What "Aura-Inspired Skincare" Means
        </h3>
        <p className="text-gray-600 mb-4">
          In Divine Aura, aura colors represent <strong>care zones</strong> — the areas of your skin, scalp, and hair that need the most support right now. Your result helps guide you toward the rituals and products best suited to your current needs.
        </p>
        
        <h4 className="text-lg font-semibold text-gray-800 mt-6 mb-4">How it works</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { step: "Step 1", text: "Take your analysis" },
            { step: "Step 2", text: "Discover your primary and supporting aura" },
            { step: "Step 3", text: "Get your ritual and product recommendations" },
            { step: "Step 4", text: "Shop or customize" },
          ].map((item, i) => (
            <div key={i} className="p-3 bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl border border-purple-100 text-center">
              <p className="text-xs font-semibold text-purple-600 mb-1">{item.step}</p>
              <p className="text-sm text-gray-700">{item.text}</p>
            </div>
          ))}
        </div>
      </div>
      
      <div className="divine-card mb-6">
        <h3 className="text-lg font-bold text-gray-800 mb-4">The 8 Aura Care Zones</h3>
        <div className="space-y-3">
          {orderedAuras.map((key) => {
            const aura = AURA_COPY[key];
            return (
              <div 
                key={key} 
                className={`p-4 rounded-xl border border-gray-100 ${aura.softBgClass || 'bg-gray-50'}`}
              >
                <div className="flex items-start gap-3">
                  <div 
                    className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ background: AURA_META[key]?.gradient }}
                  >
                    <span className="text-white text-xs font-bold">{key.charAt(0).toUpperCase()}</span>
                  </div>
                  <div>
                    <h4 className={`font-semibold ${aura.accentClass || 'text-gray-800'}`}>{aura.label}</h4>
                    <p className="text-sm text-gray-700 font-medium mt-1"><strong>Meaning:</strong> {aura.meaning}</p>
                    <p className="text-sm text-gray-600 mt-1"><strong>What it signals:</strong> {aura.whatItSignals}</p>
                    <p className="text-sm text-gray-500 mt-1"><strong>Product direction:</strong> {aura.productDirection}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      
      <div className="divine-card mb-8 bg-gradient-to-r from-purple-50 via-pink-50 to-red-50 border-pink-100">
        <div className="flex items-start gap-3">
          <Shield className="w-6 h-6 text-pink-500 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-semibold text-gray-800 mb-2">Understanding Your Results</h4>
            <p className="text-sm text-gray-600 mb-2">
              <strong>Primary Aura</strong> = Your main concern area needing the most improvement right now.
            </p>
            <p className="text-sm text-gray-600">
              <strong>Supporting Aura</strong> = Your next important area needing reinforcement.
            </p>
          </div>
        </div>
      </div>
      
      <div className="text-center">
        <button onClick={onContinue} className="btn-divine" data-testid="continue-to-mode-btn">
          I Understand, Let's Begin
          <ChevronRight className="inline-block ml-2 w-5 h-5" />
        </button>
      </div>
    </motion.div>
  );
};

// Bold Image Privacy Note
const BoldImagePrivacyNote = () => (
  <div className="mt-4 p-4 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl flex items-start gap-3 border border-emerald-100">
    <Shield className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
    <p className="text-sm text-emerald-700">
      <strong>Privacy First:</strong> Uploaded images are used only temporarily for analysis and are <strong>deleted immediately after analysis</strong>.
    </p>
  </div>
);

// Product Recommendation Card with Shop Now / Customize for Me
// Helper function to track recommendation actions
async function trackRecommendationAction(payload) {
  try {
    await axios.post(`${API}/recommendation-action`, payload);
    console.log("Recommendation action tracked:", payload.actionType, payload.productName);
  } catch (error) {
    console.warn("Recommendation tracking failed:", error);
  }
}

// Build report context for tracking
function buildReportContext(form, result) {
  const primaryMeta = AURA_META[result?.primary] || {};
  const secondaryMeta = AURA_META[result?.secondary] || {};
  const energyMeta = ENERGY_STATE_META[result?.energyState] || {};
  const topPriorities = buildTopPriorities(form) || [];
  const routineStrategy = buildRoutineStrategy(result?.primary, result?.secondary, result?.energyState) || [];
  
  const skinConcerns = (form.skin_concerns || []).concat(form.skin_goals || []);
  const hairConcerns = (form.hair_concerns || []).concat(form.hair_feel_goals || []);
  
  return {
    submissionId: form.submissionId || null,
    customerName: form.name || '',
    phone: form.phone || '',
    email: form.email || '',
    analysisMode: form.analysisMode || '',
    primaryAura: primaryMeta.label || result?.primary || '',
    secondaryAura: secondaryMeta.label || result?.secondary || '',
    energyState: energyMeta.label || result?.energyState || '',
    topSkinConcerns: skinConcerns.length > 0 ? skinConcerns : topPriorities.filter(p => !p.toLowerCase().includes('hair')),
    topHairConcerns: hairConcerns.length > 0 ? hairConcerns : topPriorities.filter(p => p.toLowerCase().includes('hair')),
    careStrategy: routineStrategy,
  };
}

// Product Recommendation Card with Shop Now / Customize for Me (with tracking)
const ProductRecommendationCard = ({ item, form, result, onOpenCustomize }) => {
  const hasProductUrl = item.productUrl && item.productUrl !== "#";
  
  const handleClick = async (e) => {
    e.preventDefault();
    
    // Track the action
    await trackRecommendationAction({
      actionType: hasProductUrl ? "shop_now_clicked" : "customize_clicked",
      productName: item.name,
      productUrl: item.productUrl || null,
      timestamp: new Date().toISOString(),
      reportContext: buildReportContext(form, result),
    });
    
    if (hasProductUrl) {
      // Open the product URL
      window.open(item.productUrl, '_blank', 'noopener,noreferrer');
    } else {
      // Open Customize page and scroll to top
      scrollToTopOfPage();
      if (onOpenCustomize) {
        onOpenCustomize(item);
      }
    }
  };
  
  return (
    <div className="p-4 bg-white rounded-xl border border-gray-100 shadow-sm" data-testid={`product-${item.name.replace(/\s+/g, '-').toLowerCase()}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1">
          <h4 className="font-semibold text-gray-800">{item.name}</h4>
          {item.shortDescription && (
            <p className="text-sm text-gray-500 mt-1">{item.shortDescription}</p>
          )}
        </div>
        
        {/* Standardized Shop Now Button - same style for all */}
        <button
          onClick={handleClick}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium text-white transition-all hover:opacity-90 flex-shrink-0"
          style={{ background: 'linear-gradient(135deg, #9B7ED9 0%, #EC4899 100%)' }}
          data-testid={`shop-now-${item.name.replace(/\s+/g, '-').toLowerCase()}`}
        >
          <ExternalLink className="w-4 h-4" />
          Shop Now
        </button>
      </div>
    </div>
  );
};

// Section Header with Ask the Doctor
const SectionHeaderWithDoctor = ({ title, subtitle, onAskDoctor, askDermat }) => (
  <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
    <div>
      <h3 className="text-xl font-bold text-gray-800">{title}</h3>
      {subtitle && <p className="text-sm text-gray-500">{subtitle}</p>}
    </div>
    <button
      onClick={onAskDoctor}
      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
        askDermat 
          ? "bg-gradient-to-r from-orange-400 to-amber-400 text-white" 
          : "border border-orange-300 text-orange-600 hover:bg-orange-50"
      }`}
      data-testid="ask-doctor-btn"
    >
      <Stethoscope className="w-4 h-4" />
      {askDermat ? "Review Requested" : "Ask the Doctor"}
    </button>
  </div>
);

// Share Footer CTA (replaces large share card)
const ShareFooterCTA = ({ shareUrl }) => {
  const shareText = "I loved this analysis. Let your friends discover and repair their Divine Aura too.";

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Divine Aura Analysis",
          text: shareText,
          url: shareUrl || window.location.href,
        });
      } catch (err) {
        // User cancelled or error
        navigator.clipboard.writeText(`${shareText} ${shareUrl || window.location.href}`);
        alert("Share link copied to clipboard!");
      }
    } else {
      navigator.clipboard.writeText(`${shareText} ${shareUrl || window.location.href}`);
      alert("Share link copied to clipboard!");
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="mt-8 p-6 bg-gradient-to-r from-purple-50 via-pink-50 to-red-50 rounded-2xl border border-pink-100 text-center"
      data-testid="share-footer-cta"
    >
      <h4 className="text-lg font-semibold text-gray-800 mb-2">Loved this analysis?</h4>
      <p className="text-gray-600 text-sm mb-4">
        Let your friends discover and repair their Divine Aura too.
      </p>
      <button
        onClick={handleNativeShare}
        className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-500 via-pink-500 to-red-500 text-white rounded-full font-medium hover:opacity-90 transition-all"
        data-testid="share-friends-btn"
      >
        <Share2 className="w-5 h-5" />
        Share with Friends
      </button>
    </motion.div>
  );
};

// Doctor Review Request helper
async function submitDoctorReviewRequest(summary) {
  try {
    const response = await axios.post(`${API}/doctor-review-request`, {
      submissionId: summary.submissionId,
      customerName: summary.customerName,
      phone: summary.phone,
      email: summary.email,
      analysisMode: summary.analysisMode,
      primaryAura: summary.primaryAura,
      primaryMeaning: summary.primaryMeaning,
      secondaryAura: summary.secondaryAura,
      secondaryMeaning: summary.secondaryMeaning,
      energyState: summary.energyState,
      topSkinConcerns: summary.topSkinConcerns,
      topHairConcerns: summary.topHairConcerns,
      careStrategy: summary.careStrategy,
    });
    return response.data.success;
  } catch (error) {
    console.error("Doctor review request failed:", error);
    return false;
  }
}

// Ask Doctor Section with proper request flow
const AskDoctorSection = ({ summary }) => {
  const [requested, setRequested] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleRequest = async () => {
    setLoading(true);
    const success = await submitDoctorReviewRequest(summary);
    setLoading(false);

    if (success) {
      setRequested(true);
    } else {
      alert("Unable to submit review request right now. Please try again.");
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="result-card mb-8"
      data-testid="ask-doctor-section"
    >
      <h3 className="text-xl font-bold text-gray-800 mb-4">Your Top Skin & Hair Concerns</h3>
      
      {requested ? (
        <div className="p-4 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl border border-emerald-100 mb-4">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="w-6 h-6 text-emerald-500 flex-shrink-0" />
            <p className="text-emerald-700">
              Review requested. Our team will review your analysis and contact you on WhatsApp or email within 24–48 hours.
            </p>
          </div>
        </div>
      ) : (
        <p className="text-gray-600 mb-4">
          Need more guidance? Request a personalized review from our team.
        </p>
      )}
      
      <button
        onClick={handleRequest}
        disabled={requested || loading}
        className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
          requested
            ? "bg-gradient-to-r from-emerald-400 to-teal-400 text-white cursor-default"
            : "bg-gradient-to-r from-orange-400 to-amber-400 text-white hover:opacity-90"
        } ${loading ? "opacity-70" : ""}`}
        data-testid="ask-doctor-btn"
      >
        <Stethoscope className="w-4 h-4" />
        {requested ? "Review Requested" : loading ? "Requesting..." : "Ask the Doctor"}
      </button>
    </motion.div>
  );
};

// Customize For Me Page
const CustomizeForMePage = ({ item, summary, onBack }) => {
  const topConcerns = useMemo(() => {
    const combined = [
      ...(summary.topSkinConcerns || []),
      ...(summary.topHairConcerns || []),
    ];
    return combined.slice(0, 6);
  }, [summary]);

  // Build WhatsApp URL directly with summary data
  const whatsAppMessage = useMemo(() => {
    const lines = [
      "Hello Divine Aura,",
      "",
      "I would like to customize this recommendation:",
      item.name,
      "",
      "My analysis summary:",
      summary.customerName ? `Name: ${summary.customerName}` : "",
      summary.phone ? `Phone: ${summary.phone}` : "",
      `Primary Concern: ${summary.primaryAura || 'N/A'}`,
      summary.primaryMeaning ? summary.primaryMeaning : "",
      summary.secondaryAura ? `Secondary Concern: ${summary.secondaryAura}` : "",
      summary.energyState ? `Energy State: ${summary.energyState}` : "",
      topConcerns.length > 0 ? `Top Concerns: ${topConcerns.join(", ")}` : "",
      "",
      "Please share details, pricing, and next steps on WhatsApp."
    ].filter(Boolean);
    return lines.join("\n");
  }, [item.name, summary, topConcerns]);

  const whatsAppUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(whatsAppMessage)}`;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="max-w-2xl mx-auto px-4 md:px-6 py-8 pb-32 md:pb-12"
      data-testid="customize-page"
    >
      <button
        onClick={onBack}
        className="inline-flex items-center gap-2 text-gray-600 hover:text-purple-600 mb-6 transition-colors"
        data-testid="back-to-results"
      >
        <ArrowLeft className="w-5 h-5" />
        Back to recommendations
      </button>

      <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-4">
        Customize This Recommendation
      </h2>
      <p className="text-gray-600 mb-8">
        This recommendation is not currently available as a ready-to-buy product on the website. We'd love to customize it for you based on your analysis.
      </p>

      <div className="divine-card mb-6">
        <h3 className="text-xl font-bold text-gray-800 mb-2">{item.name}</h3>
        {item.shortDescription && (
          <p className="text-gray-500">{item.shortDescription}</p>
        )}
      </div>

      <div className="divine-card mb-6">
        <h4 className="text-sm font-medium text-gray-500 mb-1">Primary Concern Aura</h4>
        <p className="text-lg font-semibold text-purple-600">{summary.primaryAura}</p>
        {summary.primaryMeaning && (
          <p className="text-gray-600 mt-2 text-sm">{summary.primaryMeaning}</p>
        )}
      </div>

      {summary.secondaryAura && (
        <div className="divine-card mb-6">
          <h4 className="text-sm font-medium text-gray-500 mb-1">Supporting Aura</h4>
          <p className="text-lg font-semibold text-indigo-600">{summary.secondaryAura}</p>
          {summary.secondaryMeaning && (
            <p className="text-gray-600 mt-2 text-sm">{summary.secondaryMeaning}</p>
          )}
        </div>
      )}

      {topConcerns.length > 0 && (
        <div className="divine-card mb-6">
          <h3 className="text-lg font-bold text-gray-800 mb-3">Top concerns from your analysis</h3>
          <div className="flex flex-wrap gap-2">
            {topConcerns.map((concern, i) => (
              <span key={i} className="px-3 py-1 bg-gradient-to-r from-purple-50 via-pink-50 to-red-50 text-pink-700 rounded-full text-sm border border-pink-100">
                {concern}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="divine-card mb-8 bg-gradient-to-r from-amber-50 to-yellow-50 border-amber-100">
        <h3 className="text-lg font-bold text-gray-800 mb-4">What happens next</h3>
        <ul className="space-y-3 text-gray-700">
          <li className="flex items-start gap-2">
            <span className="text-amber-500 mt-1">•</span>
            Our team reviews your analysis and recommendation.
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-500 mt-1">•</span>
            We confirm the best-fit customized option for you.
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-500 mt-1">•</span>
            We share pricing, timeline, and next steps on WhatsApp.
          </li>
          <li className="flex items-start gap-2">
            <span className="text-amber-500 mt-1">•</span>
            Your product is freshly prepared on order.
          </li>
        </ul>
      </div>

      <a
        href={whatsAppUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="btn-divine w-full flex items-center justify-center gap-2"
        data-testid="continue-whatsapp-btn"
      >
        <MessageCircle className="w-5 h-5" />
        Continue on WhatsApp
      </a>
    </motion.div>
  );
};

// Hero Section
const HeroSection = ({ onStart }) => (
  <motion.section 
    className="min-h-screen flex flex-col items-center justify-start pt-8 md:justify-center px-4 pb-28 md:pb-6 md:px-8 md:py-12"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    data-testid="hero-section"
  >
    <div className="max-w-2xl mx-auto text-center">
      {/* Logo with subtle glow */}
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="mb-4 md:mb-5 relative"
      >
        {/* Subtle aura glow behind logo */}
        <div 
          className="absolute inset-0 mx-auto rounded-full blur-3xl opacity-25"
          style={{
            width: '70%',
            height: '70%',
            top: '15%',
            left: '15%',
            background: 'radial-gradient(ellipse at center, rgba(155, 126, 217, 0.4) 0%, rgba(99, 102, 241, 0.3) 25%, rgba(96, 165, 250, 0.2) 50%, rgba(52, 211, 153, 0.15) 75%, transparent 100%)'
          }}
        />
        <img 
          src="/divine-aura-logo-cropped.png" 
          alt="Divine Aura - Your Aura In Bloom" 
          className="w-[255px] h-auto md:w-[300px] lg:w-[340px] mx-auto object-contain relative z-10"
          style={{ mixBlendMode: 'multiply' }}
          data-testid="hero-logo"
        />
      </motion.div>
      
      {/* Gradient Heading */}
      <motion.h1
        initial={{ y: 10, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.15 }}
        className="text-[1.85rem] md:text-4xl lg:text-5xl font-semibold mb-2 md:mb-4 leading-tight"
        style={{
          background: 'linear-gradient(90deg, #9B7ED9 0%, #6366F1 14%, #60A5FA 28%, #34D399 42%, #FBBF24 57%, #FB923C 71%, #EC4899 85%, #EF4444 100%)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text'
        }}
      >
        Discover Your Divine Aura
      </motion.h1>
      
      <motion.p
        initial={{ y: 10, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.25 }}
        className="text-[0.95rem] md:text-lg text-gray-600 mb-5 md:mb-6 leading-relaxed px-2"
      >
        A personalized skin, hair and scalp analysis designed to reveal what your beauty truly needs — gently, intuitively, and uniquely yours.
      </motion.p>
      
      <motion.div
        initial={{ y: 10, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.35 }}
        className="flex flex-wrap justify-center gap-2 md:gap-3 mb-5 md:mb-6"
      >
        {[
          { icon: Zap, text: "2-min Quiz - Quick & effortless", color: "#9B7ED9" },
          { icon: Shield, text: "Your privacy, protected", color: "#C84B8A" },
          { icon: Heart, text: "Glow-focused care", color: "#E8536D" },
        ].map((item, i) => (
          <div key={i} className="flex items-center gap-1.5 px-3 py-1.5 md:px-4 md:py-2.5 bg-white/90 rounded-full shadow-sm border border-gray-100">
            <item.icon className="w-3.5 h-3.5 md:w-4 md:h-4" style={{ color: item.color }} />
            <span className="text-[0.75rem] md:text-sm text-gray-700 font-medium">{item.text}</span>
          </div>
        ))}
      </motion.div>
      
      <motion.button
        initial={{ y: 10, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.45 }}
        onClick={onStart}
        className="btn-divine text-[0.95rem] md:text-lg px-8 md:px-10 py-3.5 md:py-4"
        data-testid="start-analysis-btn"
      >
        Begin Your Analysis
        <ChevronRight className="inline-block ml-2 w-4 h-4 md:w-5 md:h-5" />
      </motion.button>
    </div>
  </motion.section>
);

// Mode Selection
const ModeSelection = ({ mode, setMode, onContinue }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="max-w-3xl mx-auto px-4 py-12 pb-32 md:pb-12"
    data-testid="mode-selection"
  >
    <div className="text-center mb-10">
      <h2 className="text-3xl font-bold text-gray-800 mb-3">Choose Your Analysis Mode</h2>
      <p className="text-gray-600">Select how you'd like to discover your Divine Aura</p>
    </div>
    
    <div className="grid md:grid-cols-3 gap-6 mb-10">
      {[
        {
          key: "questionnaire",
          icon: FileText,
          title: "Questionnaire",
          desc: "Understand your skin through guided inputs",
          color: "#6366F1",
        },
        {
          key: "digital",
          icon: Camera,
          title: "Digital Analysis",
          desc: "Quick analysis using your images",
          color: "#10B981",
        },
        {
          key: "hybrid",
          icon: Star,
          title: "Hybrid Analysis",
          desc: "Most accurate results using both inputs",
          recommended: true,
          color: "#F59E0B",
        },
      ].map((m) => (
        <motion.div
          key={m.key}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => setMode(m.key)}
          className={`mode-card relative ${mode === m.key ? "selected" : ""}`}
          data-testid={`mode-${m.key}`}
        >
          {m.recommended && (
            <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-gradient-to-r from-amber-400 to-orange-400 text-white text-xs font-semibold rounded-full flex items-center gap-1">
              <span>⭐</span> Recommended
            </span>
          )}
          <div 
            className="w-14 h-14 mx-auto mb-4 rounded-full flex items-center justify-center"
            style={{ background: `linear-gradient(135deg, ${m.color}20 0%, ${m.color}40 100%)` }}
          >
            <m.icon className="w-7 h-7" style={{ color: m.color }} />
          </div>
          <h3 className="font-semibold text-lg mb-2 text-gray-800">{m.title}</h3>
          <p className="text-sm text-gray-500">{m.desc}</p>
          {mode === m.key && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              className="absolute top-3 right-3 w-6 h-6 rounded-full flex items-center justify-center bg-gradient-to-r from-purple-500 via-pink-500 to-red-500"
            >
              <Check className="w-4 h-4 text-white" />
            </motion.div>
          )}
        </motion.div>
      ))}
    </div>
    
    <div className="text-center">
      <button onClick={onContinue} className="btn-divine" data-testid="continue-mode-btn">
        Continue
        <ChevronRight className="inline-block ml-2 w-5 h-5" />
      </button>
    </div>
  </motion.div>
);

// Option Chip Group
const OptionChipGroup = ({ title, subtitle, options, selected, onChange, max }) => (
  <div className="mb-8" data-testid={`option-group-${title.replace(/\s+/g, '-').toLowerCase()}`}>
    <h3 className="text-xl font-semibold text-gray-800 mb-2">{title}</h3>
    <p className="text-sm text-gray-500 mb-4">{subtitle}</p>
    <div className="flex flex-wrap gap-3">
      {options.map((opt) => (
        <motion.button
          key={opt}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => onChange(toggleInArray(selected, opt, max))}
          className={`option-chip ${selected.includes(opt) ? "selected" : ""}`}
          data-testid={`option-${opt.replace(/\s+/g, '-').toLowerCase()}`}
        >
          {opt}
          {selected.includes(opt) && <Check className="inline-block ml-2 w-4 h-4" />}
        </motion.button>
      ))}
    </div>
  </div>
);

// Single Select Group
const SingleSelectGroup = ({ title, subtitle, options, selected, onChange }) => (
  <div className="mb-8" data-testid={`single-select-${title.replace(/\s+/g, '-').toLowerCase()}`}>
    <h3 className="text-xl font-semibold text-gray-800 mb-2">{title}</h3>
    <p className="text-sm text-gray-500 mb-4">{subtitle}</p>
    <div className="flex flex-wrap gap-3">
      {options.map((opt) => (
        <motion.button
          key={opt}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => onChange(selected === opt ? "" : opt)}
          className={`option-chip ${selected === opt ? "selected" : ""}`}
          data-testid={`option-${opt.replace(/\s+/g, '-').toLowerCase()}`}
        >
          {opt}
          {selected === opt && <Check className="inline-block ml-2 w-4 h-4" />}
        </motion.button>
      ))}
    </div>
  </div>
);

// Upload Zone (for Hybrid mode - keeps existing functionality)
const UploadZone = ({ form, setForm, isDigitalOnly = false }) => {
  const handleFile = (key, file) => {
    setForm((prev) => ({ ...prev, [key]: file || null }));
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="divine-card mb-8"
      data-testid="upload-zone"
    >
      <h3 className="text-xl font-semibold text-gray-800 mb-2">
        {isDigitalOnly ? "Upload Your Images" : "Add images for deeper insight"}
      </h3>
      <p className="text-sm text-gray-500 mb-6">
        {isDigitalOnly 
          ? "We'll analyze your images to discover your Divine Aura"
          : "Upload a clear face image and optional hair or scalp image for enhanced analysis."
        }
      </p>
      
      <div className="grid md:grid-cols-2 gap-4 md:gap-6">
        <div className="border-2 border-dashed border-indigo-200 rounded-2xl p-5 md:p-6 text-center hover:border-indigo-300 transition-colors bg-indigo-50/30">
          <Camera className="w-10 h-10 mx-auto mb-3 text-indigo-400" />
          <p className="font-medium text-gray-700 mb-2">Face Image</p>
          <p className="text-xs text-gray-500 mb-4">Frontal, natural-light image</p>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => handleFile("imageFace", e.target.files?.[0])}
            className="hidden"
            id="face-upload"
            data-testid="face-upload-input"
          />
          <label htmlFor="face-upload" className="inline-flex items-center px-4 py-2 border-2 border-indigo-300 text-indigo-600 rounded-full text-sm font-medium cursor-pointer hover:bg-indigo-50 transition-colors">
            <Upload className="inline-block w-4 h-4 mr-2" />
            {form.imageFace ? form.imageFace.name.substring(0, 15) + "..." : "Choose File"}
          </label>
          {form.imageFace && (
            <button
              onClick={() => handleFile("imageFace", null)}
              className="ml-2 text-red-400 hover:text-red-500"
              data-testid="remove-face-image"
            >
              <Trash2 className="inline-block w-4 h-4" />
            </button>
          )}
        </div>
        
        <div className="border-2 border-dashed border-emerald-200 rounded-2xl p-5 md:p-6 text-center hover:border-emerald-300 transition-colors bg-emerald-50/30">
          <ScanFace className="w-10 h-10 mx-auto mb-3 text-emerald-400" />
          <p className="font-medium text-gray-700 mb-2">Hair / Scalp Image</p>
          <p className="text-xs text-gray-500 mb-4">Scalp or hair-length image</p>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => handleFile("imageHair", e.target.files?.[0])}
            className="hidden"
            id="hair-upload"
            data-testid="hair-upload-input"
          />
          <label htmlFor="hair-upload" className="inline-flex items-center px-4 py-2 border-2 border-emerald-300 text-emerald-600 rounded-full text-sm font-medium cursor-pointer hover:bg-emerald-50 transition-colors">
            <Upload className="inline-block w-4 h-4 mr-2" />
            {form.imageHair ? form.imageHair.name.substring(0, 15) + "..." : "Choose File"}
          </label>
          {form.imageHair && (
            <button
              onClick={() => handleFile("imageHair", null)}
              className="ml-2 text-red-400 hover:text-red-500"
              data-testid="remove-hair-image"
            >
              <Trash2 className="inline-block w-4 h-4" />
            </button>
          )}
        </div>
      </div>
      
      <div className="mt-6 p-4 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl flex items-start gap-3 border border-emerald-100">
        <Shield className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-emerald-700">
          <strong>Privacy First:</strong> Uploaded images are used only temporarily for analysis and are <strong>deleted immediately after analysis</strong>.
        </p>
      </div>
    </motion.div>
  );
};

// ============ DIGITAL CAMERA CAPTURE COMPONENT ============
const DigitalCaptureStep = ({ form, setForm, onContinue, loading }) => {
  const [captureMode, setCaptureMode] = useState(null); // 'camera' or 'upload'
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraReady, setCameraReady] = useState(false); // Track when video is actually playing
  const [cameraError, setCameraError] = useState(null);
  const [capturedPreview, setCapturedPreview] = useState(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const canvasRef = useRef(null);

  // Convert data URL to File object
  const dataURLtoFile = (dataurl, filename) => {
    try {
      if (!dataurl || typeof dataurl !== 'string' || !dataurl.includes(',')) {
        console.error('Invalid data URL');
        return null;
      }
      const arr = dataurl.split(',');
      if (arr.length < 2) {
        console.error('Invalid data URL format');
        return null;
      }
      const mimeMatch = arr[0].match(/:(.*?);/);
      if (!mimeMatch) {
        console.error('Could not extract MIME type');
        return null;
      }
      const mime = mimeMatch[1];
      const bstr = atob(arr[1]);
      let n = bstr.length;
      const u8arr = new Uint8Array(n);
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
      }
      return new File([u8arr], filename, { type: mime });
    } catch (err) {
      console.error('Error converting data URL to file:', err);
      return null;
    }
  };

  // Start camera
  const startCamera = async () => {
    setCameraError(null);
    setCameraReady(false);
    setCameraActive(true);
    
    try {
      // Check if getUserMedia is supported
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setCameraError('Camera not supported in this browser. Please use the upload option.');
        setCameraActive(false);
        return;
      }

      const constraints = {
        video: {
          facingMode: 'user', // Front camera
          width: { ideal: 640 },
          height: { ideal: 640 }
        },
        audio: false
      };
      
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        
        // Wait for video to be ready
        videoRef.current.onloadedmetadata = () => {
          videoRef.current.play()
            .then(() => {
              setCameraReady(true);
            })
            .catch(err => {
              console.error('Video play error:', err);
              setCameraError('Could not start video. Please try the upload option.');
            });
        };
      }
    } catch (err) {
      console.error('Camera error:', err);
      setCameraActive(false);
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setCameraError('Camera access denied. Please allow camera access in your browser settings, or use the upload option.');
      } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        setCameraError('No camera found. Please use the upload option instead.');
      } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
        setCameraError('Camera is in use by another app. Please close other apps or use the upload option.');
      } else {
        setCameraError(`Could not access camera: ${err.message || 'Unknown error'}. Please try the upload option.`);
      }
    }
  };

  // Stop camera
  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setCameraActive(false);
    setCameraReady(false);
  };

  // Capture photo from camera
  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) {
      setCameraError('Camera not ready. Please wait or try again.');
      return;
    }
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    
    // Check if video has valid dimensions
    if (!video.videoWidth || !video.videoHeight) {
      setCameraError('Camera stream not ready. Please wait a moment and try again.');
      return;
    }
    
    const ctx = canvas.getContext('2d');
    
    // Set canvas size to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    
    // Draw the video frame to canvas (flip horizontally to match mirror view)
    ctx.translate(canvas.width, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    ctx.setTransform(1, 0, 0, 1, 0, 0); // Reset transform
    
    // Convert to data URL
    const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
    
    if (!dataUrl || dataUrl === 'data:,') {
      setCameraError('Could not capture image. Please try again.');
      return;
    }
    
    setCapturedPreview(dataUrl);
    
    // Convert to File and set in form
    const file = dataURLtoFile(dataUrl, `face-capture-${Date.now()}.jpg`);
    if (file) {
      setForm(prev => ({ ...prev, imageFace: file, capturedImageUrl: dataUrl }));
    } else {
      setCameraError('Could not process captured image. Please try the upload option.');
      return;
    }
    
    // Stop camera after capture
    stopCamera();
  };

  // Retake photo
  const retakePhoto = () => {
    setCapturedPreview(null);
    setForm(prev => ({ ...prev, imageFace: null, capturedImageUrl: null }));
    setCameraError(null);
    startCamera();
  };

  // Handle file upload
  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      setCapturedPreview(previewUrl);
      setForm(prev => ({ ...prev, imageFace: file, capturedImageUrl: previewUrl }));
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopCamera();
      if (capturedPreview && capturedPreview.startsWith('blob:')) {
        URL.revokeObjectURL(capturedPreview);
      }
    };
  }, []);

  // Scroll to top when this step loads
  useEffect(() => {
    scrollToTopOfPage();
  }, []);

  // If we have a captured/uploaded image, show preview with direct CTA
  if (capturedPreview || form.imageFace) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="divine-card mb-6"
        data-testid="digital-capture-preview"
      >
        {/* Logo at top - slightly larger for branding */}
        <div className="text-center mb-4">
          <img 
            src="/divine-aura-result-logo.png" 
            alt="Divine Aura" 
            className="w-28 h-28 mx-auto object-contain"
          />
        </div>
        
        <h3 className="text-xl font-semibold text-gray-800 mb-4 text-center">Your Photo</h3>
        
        <div className="relative mx-auto max-w-xs">
          <div className="aspect-square rounded-2xl overflow-hidden border-4 border-purple-200 shadow-lg">
            <img 
              src={capturedPreview || (form.capturedImageUrl)} 
              alt="Captured face" 
              className="w-full h-full object-cover"
            />
          </div>
          
          {/* Overlay guide for face positioning */}
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute inset-4 border-2 border-dashed border-white/50 rounded-full"></div>
          </div>
        </div>
        
        {/* Retake option */}
        <div className="flex justify-center mt-4">
          <button
            onClick={() => {
              setCapturedPreview(null);
              setForm(prev => ({ ...prev, imageFace: null, capturedImageUrl: null }));
              setCaptureMode(null);
            }}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors"
            data-testid="retake-btn"
          >
            <RotateCcw className="w-4 h-4" />
            Retake Photo
          </button>
        </div>
        
        {/* Subtle privacy note */}
        <div className="mt-5 flex items-center justify-center gap-2 text-sm text-emerald-600">
          <Shield className="w-4 h-4" />
          <span>Your photo is analyzed locally and deleted after analysis.</span>
        </div>
        
        {/* Single main CTA - Reveal My Divine Aura */}
        {loading ? (
          <div className="text-center py-6 mt-5">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-r from-purple-400 via-pink-400 to-red-400 animate-pulse" />
            <p className="text-gray-600 font-medium">Analyzing your divine aura...</p>
            <p className="text-sm text-gray-500 mt-1">This only takes a moment</p>
          </div>
        ) : (
          <button
            onClick={onContinue}
            className="btn-divine w-full mt-5"
            data-testid="reveal-aura-btn"
          >
            <Sparkles className="inline-block w-5 h-5 mr-2" />
            Reveal My Divine Aura
          </button>
        )}
      </motion.div>
    );
  }

  // Camera view when active
  if (cameraActive) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="divine-card mb-6"
        data-testid="digital-camera-view"
      >
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold text-gray-800">Position Your Face</h3>
          <button
            onClick={() => {
              stopCamera();
              setCaptureMode(null);
              setCameraError(null);
            }}
            className="p-2 text-gray-500 hover:text-gray-700 rounded-full hover:bg-gray-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Camera error message */}
        {cameraError && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600">
            <AlertCircle className="inline-block w-4 h-4 mr-2" />
            {cameraError}
          </div>
        )}
        
        {/* Camera guidance */}
        <div className="mb-4 p-3 bg-amber-50 rounded-xl border border-amber-200">
          <div className="flex items-center gap-2 text-amber-700 text-sm">
            <Focus className="w-4 h-4" />
            <span><strong>Tips:</strong> Face centered • Good lighting • Look straight</span>
          </div>
        </div>
        
        <div className="relative mx-auto max-w-sm">
          <div className="aspect-square rounded-2xl overflow-hidden bg-gray-900 relative">
            {/* Loading state */}
            {!cameraReady && (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-900 z-10">
                <div className="text-center">
                  <div className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
                  <p className="text-white text-sm">Starting camera...</p>
                </div>
              </div>
            )}
            
            <video 
              ref={videoRef} 
              autoPlay 
              playsInline 
              muted 
              className="w-full h-full object-cover transform scale-x-[-1]"
            />
            
            {/* Face guide overlay - only show when camera is ready */}
            {cameraReady && (
              <div className="absolute inset-0 pointer-events-none">
                <div className="absolute inset-8 border-2 border-dashed border-white/60 rounded-full"></div>
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-2 h-2 bg-white/80 rounded-full"></div>
              </div>
            )}
          </div>
          
          <canvas ref={canvasRef} className="hidden" />
        </div>
        
        <div className="flex flex-col items-center mt-6">
          <button
            onClick={capturePhoto}
            disabled={!cameraReady}
            className={`w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center shadow-lg transition-all active:scale-95 ${!cameraReady ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-90'}`}
            data-testid="capture-photo-btn"
          >
            <CircleDot className="w-8 h-8 text-white" />
          </button>
          
          <p className="text-center text-sm text-gray-500 mt-3">
            {cameraReady ? 'Tap the button to capture' : 'Please wait for camera to start...'}
          </p>
          
          {/* Fallback to upload */}
          <button
            onClick={() => {
              stopCamera();
              setCaptureMode(null);
            }}
            className="mt-4 text-sm text-purple-600 hover:text-purple-700 underline"
          >
            Use upload instead
          </button>
        </div>
      </motion.div>
    );
  }

  // Initial mode selection
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="divine-card mb-6"
      data-testid="digital-capture-step"
    >
      <div className="text-center mb-6">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-r from-purple-100 to-pink-100 flex items-center justify-center">
          <Camera className="w-8 h-8 text-purple-600" />
        </div>
        <h3 className="text-xl font-semibold text-gray-800 mb-2">Capture Your Face</h3>
        <p className="text-sm text-gray-500">Choose how you'd like to provide your photo</p>
      </div>
      
      {cameraError && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600">
          <AlertCircle className="inline-block w-4 h-4 mr-2" />
          {cameraError}
        </div>
      )}
      
      <div className="grid grid-cols-2 gap-4">
        {/* Use Camera Option */}
        <button
          onClick={() => {
            setCaptureMode('camera');
            startCamera();
          }}
          className="p-6 border-2 border-purple-200 rounded-2xl hover:border-purple-400 hover:bg-purple-50/50 transition-all text-center group"
          data-testid="use-camera-btn"
        >
          <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-purple-100 flex items-center justify-center group-hover:bg-purple-200 transition-colors">
            <Video className="w-7 h-7 text-purple-600" />
          </div>
          <p className="font-semibold text-gray-800">Use Camera</p>
          <p className="text-xs text-gray-500 mt-1">Take a live photo</p>
        </button>
        
        {/* Upload Picture Option */}
        <label
          htmlFor="digital-face-upload"
          className="p-6 border-2 border-pink-200 rounded-2xl hover:border-pink-400 hover:bg-pink-50/50 transition-all text-center cursor-pointer group"
          data-testid="upload-picture-btn"
        >
          <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-pink-100 flex items-center justify-center group-hover:bg-pink-200 transition-colors">
            <Image className="w-7 h-7 text-pink-600" />
          </div>
          <p className="font-semibold text-gray-800">Upload Picture</p>
          <p className="text-xs text-gray-500 mt-1">Choose from gallery</p>
        </label>
        <input
          type="file"
          accept="image/*"
          onChange={handleFileUpload}
          className="hidden"
          id="digital-face-upload"
          data-testid="digital-face-upload-input"
        />
      </div>
      
      {/* Capture Guidelines */}
      <div className="mt-6 p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border border-purple-100">
        <h4 className="font-semibold text-gray-800 mb-2 flex items-center gap-2">
          <Focus className="w-4 h-4 text-purple-500" />
          For best results:
        </h4>
        <ul className="text-sm text-gray-600 space-y-1">
          <li>• Face centered in frame</li>
          <li>• Good, even lighting</li>
          <li>• Minimal tilt, look straight</li>
          <li>• Remove shadows if possible</li>
        </ul>
      </div>
      
      <div className="mt-4 p-4 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl flex items-start gap-3 border border-emerald-100">
        <Shield className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-emerald-700">
          <strong>Privacy First:</strong> Your photo is used only for analysis and <strong>deleted immediately after</strong>.
        </p>
      </div>
      
      {/* Digital Analysis Disclaimer */}
      <div className="mt-3 p-3 bg-gray-50/80 rounded-xl border border-gray-100" data-testid="digital-disclaimer">
        <p className="text-xs text-gray-500 leading-relaxed">
          <span className="font-medium text-gray-600">Note:</span> Digital analysis is based on the visible appearance of your skin in the captured/uploaded image. Results may vary slightly depending on lighting, angle, camera quality, facial expression, shadows, and image clarity. For more accurate results, use good lighting and try <span className="text-purple-600 font-medium">Hybrid Analysis</span>.
        </p>
      </div>
    </motion.div>
  );
};

// Progress Header
const ProgressHeader = ({ currentStep, totalSteps, onBack }) => (
  <div className="sticky top-0 z-50 bg-white/90 backdrop-blur-lg border-b border-rose-100 px-4 py-4" data-testid="progress-header">
    <div className="max-w-3xl mx-auto">
      <div className="flex items-center justify-between mb-3">
        <button 
          onClick={onBack} 
          className="flex items-center gap-2 text-gray-500 hover:text-gray-700 transition-colors"
          data-testid="back-btn"
        >
          <ChevronLeft className="w-5 h-5" />
          <span className="text-sm">Back</span>
        </button>
        <Logo />
        <span className="text-sm text-gray-500">Step {currentStep} of {totalSteps}</span>
      </div>
      <Progress value={(currentStep / totalSteps) * 100} className="h-2 bg-rose-100" />
    </div>
  </div>
);

// Aura Card
const AuraCard = ({ auraKey, isPrimary = false }) => {
  const meta = AURA_META[auraKey];
  if (!meta) return null;
  const Icon = meta.icon;
  
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      className={`divine-card overflow-hidden ${isPrimary ? "ring-2 ring-rose-300" : ""}`}
      data-testid={`aura-card-${auraKey}`}
    >
      <div className="h-3 w-full" style={{ background: meta.gradient }} />
      <div className="p-6">
        <div className="flex items-center gap-4 mb-4">
          <div 
            className="w-14 h-14 rounded-full flex items-center justify-center shadow-lg"
            style={{ background: meta.gradient }}
          >
            <Icon className="w-7 h-7 text-white" />
          </div>
          <div>
            <p className="text-sm text-gray-500">{isPrimary ? "Your Primary Aura" : "Your Supporting Aura"}</p>
            <h3 className="text-xl font-bold text-gray-800">{meta.label}</h3>
          </div>
        </div>
        <p className="text-lg font-medium text-gray-700 mb-3">{meta.theme}</p>
        <p className="text-gray-600">{meta.summary}</p>
      </div>
    </motion.div>
  );
};

// Energy State Card
const EnergyStateCard = ({ state }) => {
  const meta = ENERGY_STATE_META[state];
  if (!meta) return null;
  const Icon = meta.icon;
  
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      className="divine-card"
      data-testid="energy-state-card"
    >
      <div className="flex items-center gap-4 mb-4">
        <div 
          className="w-14 h-14 rounded-full flex items-center justify-center shadow-lg"
          style={{ background: `linear-gradient(135deg, ${meta.color}40 0%, ${meta.color}80 100%)` }}
        >
          <Icon className="w-7 h-7" style={{ color: meta.color }} />
        </div>
        <div>
          <p className="text-sm text-gray-500">Your Skin Energy State</p>
          <h3 className="text-xl font-bold text-gray-800">{meta.label}</h3>
        </div>
      </div>
      <p className="text-gray-600">{meta.summary}</p>
    </motion.div>
  );
};

// Share Your Aura Card Component
const ShareAuraCard = ({ result, userName }) => {
  const primaryMeta = AURA_META[result.primary];
  const secondaryMeta = AURA_META[result.secondary];
  const energyMeta = ENERGY_STATE_META[result.energyState];
  const cardRef = React.useRef(null);
  const [showShareModal, setShowShareModal] = React.useState(false);

  const generateShareImage = async () => {
    if (!cardRef.current) return null;
    
    // Create a canvas to render the card
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    canvas.width = 600;
    canvas.height = 800;
    
    // Background gradient
    const gradient = ctx.createLinearGradient(0, 0, 600, 800);
    gradient.addColorStop(0, '#FDF8F3');
    gradient.addColorStop(0.5, '#FFF9F5');
    gradient.addColorStop(1, '#F0F7F0');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, 600, 800);
    
    // Add decorative circles
    ctx.beginPath();
    ctx.arc(500, 100, 80, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(232, 180, 184, 0.2)';
    ctx.fill();
    
    ctx.beginPath();
    ctx.arc(100, 700, 60, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(184, 201, 184, 0.2)';
    ctx.fill();
    
    // Logo/Brand
    ctx.font = 'bold 28px Georgia, serif';
    ctx.fillStyle = '#D4919A';
    ctx.textAlign = 'center';
    ctx.fillText('Divine Aura', 300, 60);
    
    ctx.font = '14px Arial, sans-serif';
    ctx.fillStyle = '#7A7A7A';
    ctx.fillText('Glow Like It\'s Yours', 300, 85);
    
    // User name
    if (userName) {
      ctx.font = '18px Arial, sans-serif';
      ctx.fillStyle = '#4A4A4A';
      ctx.fillText(`${userName}'s Aura Profile`, 300, 130);
    }
    
    // Primary Aura Circle
    const primaryGradient = ctx.createRadialGradient(300, 250, 0, 300, 250, 80);
    primaryGradient.addColorStop(0, primaryMeta.color);
    primaryGradient.addColorStop(1, primaryMeta.color + '80');
    ctx.beginPath();
    ctx.arc(300, 250, 70, 0, Math.PI * 2);
    ctx.fillStyle = primaryGradient;
    ctx.fill();
    
    // Sparkle icon placeholder (simple star)
    ctx.fillStyle = 'white';
    ctx.font = '40px Arial';
    ctx.fillText('✦', 285, 265);
    
    // Primary Aura Text
    ctx.font = 'bold 26px Georgia, serif';
    ctx.fillStyle = '#4A4A4A';
    ctx.fillText(primaryMeta.label, 300, 360);
    
    ctx.font = '16px Arial, sans-serif';
    ctx.fillStyle = primaryMeta.color;
    ctx.fillText(primaryMeta.theme, 300, 390);
    
    // Divider
    ctx.beginPath();
    ctx.moveTo(150, 430);
    ctx.lineTo(450, 430);
    ctx.strokeStyle = '#E8B4B8';
    ctx.lineWidth = 1;
    ctx.stroke();
    
    // Secondary Aura
    ctx.font = '14px Arial, sans-serif';
    ctx.fillStyle = '#7A7A7A';
    ctx.fillText('Supporting Aura', 300, 470);
    
    ctx.font = 'bold 20px Georgia, serif';
    ctx.fillStyle = '#4A4A4A';
    ctx.fillText(secondaryMeta.label, 300, 500);
    
    ctx.font = '14px Arial, sans-serif';
    ctx.fillStyle = secondaryMeta.color;
    ctx.fillText(secondaryMeta.theme, 300, 525);
    
    // Divider
    ctx.beginPath();
    ctx.moveTo(150, 560);
    ctx.lineTo(450, 560);
    ctx.strokeStyle = '#B8C9B8';
    ctx.lineWidth = 1;
    ctx.stroke();
    
    // Energy State
    ctx.font = '14px Arial, sans-serif';
    ctx.fillStyle = '#7A7A7A';
    ctx.fillText('Energy State', 300, 600);
    
    ctx.font = 'bold 20px Georgia, serif';
    ctx.fillStyle = '#4A4A4A';
    ctx.fillText(energyMeta.label, 300, 630);
    
    // Footer
    ctx.font = '12px Arial, sans-serif';
    ctx.fillStyle = '#9A9A9A';
    ctx.fillText('Discover your aura at', 300, 720);
    ctx.font = 'bold 14px Arial, sans-serif';
    ctx.fillStyle = '#D4919A';
    ctx.fillText('divineaura.com', 300, 745);
    
    return canvas.toDataURL('image/png');
  };

  const handleDownloadImage = async () => {
    const imageData = await generateShareImage();
    if (imageData) {
      const link = document.createElement('a');
      link.download = `my-divine-aura-${result.primary}.png`;
      link.href = imageData;
      link.click();
    }
  };

  const handleShareWhatsApp = async () => {
    const shareText = `✨ I discovered my Divine Aura! ✨\n\n🌸 Primary: ${primaryMeta.label} (${primaryMeta.theme})\n💜 Supporting: ${secondaryMeta.label}\n⚡ Energy: ${energyMeta.label}\n\nDiscover yours at divineaura.com`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleShareInstagram = async () => {
    // Download the image first for Instagram sharing
    await handleDownloadImage();
    alert('Your aura card has been downloaded! Open Instagram and share it to your story or feed.');
  };

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="divine-card mb-8"
        data-testid="share-aura-section"
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 via-pink-500 to-red-500 flex items-center justify-center">
            <Share2 className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-800">Share Your Aura</h3>
            <p className="text-sm text-gray-500">Let your friends discover their divine aura too</p>
          </div>
        </div>
        
        {/* Preview Card */}
        <div 
          ref={cardRef}
          className="relative overflow-hidden rounded-2xl p-6 mb-6"
          style={{ 
            background: 'linear-gradient(135deg, #FDF8F3 0%, #FFF9F5 50%, #F0F7F0 100%)',
            border: '1px solid rgba(232, 180, 184, 0.3)'
          }}
        >
          {/* Decorative elements */}
          <div className="absolute top-4 right-4 w-16 h-16 rounded-full bg-rose-100/50" />
          <div className="absolute bottom-4 left-4 w-12 h-12 rounded-full bg-green-100/50" />
          
          <div className="relative text-center">
            <p className="text-sm text-rose-400 font-medium mb-1">Divine Aura</p>
            <p className="text-xs text-gray-400 mb-4">Glow Like It's Yours</p>
            
            {/* Primary Aura */}
            <div 
              className="w-20 h-20 mx-auto rounded-full flex items-center justify-center shadow-lg mb-3"
              style={{ background: primaryMeta.gradient }}
            >
              <Sparkles className="w-10 h-10 text-white" />
            </div>
            <h4 className="text-xl font-bold text-gray-800">{primaryMeta.label}</h4>
            <p className="text-sm mb-4" style={{ color: primaryMeta.color }}>{primaryMeta.theme}</p>
            
            {/* Divider */}
            <div className="w-24 h-px bg-rose-200 mx-auto my-4" />
            
            {/* Secondary & Energy in row */}
            <div className="flex justify-center gap-8 text-center">
              <div>
                <p className="text-xs text-gray-400 mb-1">Supporting</p>
                <p className="text-sm font-semibold text-gray-700">{secondaryMeta.label}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 mb-1">Energy</p>
                <p className="text-sm font-semibold text-gray-700">{energyMeta.label}</p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Share Buttons */}
        <div className="grid grid-cols-3 gap-3">
          <button
            onClick={handleDownloadImage}
            className="flex flex-col items-center gap-2 p-4 rounded-xl border-2 border-gray-200 hover:border-purple-300 hover:bg-purple-50 transition-all"
            data-testid="download-aura-card"
          >
            <Download className="w-6 h-6 text-purple-500" />
            <span className="text-xs font-medium text-gray-600">Save Image</span>
          </button>
          
          <button
            onClick={handleShareWhatsApp}
            className="flex flex-col items-center gap-2 p-4 rounded-xl border-2 border-gray-200 hover:border-pink-300 hover:bg-pink-50 transition-all"
            data-testid="share-whatsapp"
          >
            <MessageCircle className="w-6 h-6 text-pink-500" />
            <span className="text-xs font-medium text-gray-600">WhatsApp</span>
          </button>
          
          <button
            onClick={handleShareInstagram}
            className="flex flex-col items-center gap-2 p-4 rounded-xl border-2 border-gray-200 hover:border-red-300 hover:bg-red-50 transition-all"
            data-testid="share-instagram"
          >
            <Instagram className="w-6 h-6 text-red-500" />
            <span className="text-xs font-medium text-gray-600">Instagram</span>
          </button>
        </div>
      </motion.div>
    </>
  );
};

// ============ DIGITAL-ONLY RESULTS PAGE (Concise, Image-First Layout) ============
const DigitalResultsPage = ({ form, result, onReset, onAskDermat }) => {
  // Scroll to top when results load
  useEffect(() => {
    scrollToTopOfPage();
  }, []);

  const primaryMeta = AURA_META[result?.primary];
  const secondaryMeta = AURA_META[result?.secondary];
  const primaryCopy = AURA_COPY[result?.primary];
  const secondaryCopy = AURA_COPY[result?.secondary];
  const primaryKit = AURA_KIT_PRODUCTS[result?.primary];
  const secondaryKit = AURA_KIT_PRODUCTS[result?.secondary];
  
  // Get detected concerns based on flags - COMPLETE mapping for all Digital analysis flags
  const detectedConcerns = useMemo(() => {
    const concerns = [];
    const flags = form.detectedFlags || [];
    
    // ============ PURPLE AURA CONCERNS (Aging/Repair) ============
    if (flags.includes("lines")) concerns.push({ label: "Fine Lines / Wrinkles", position: "eyes" });
    if (flags.includes("undereyeLines")) concerns.push({ label: "Under-Eye Fine Lines", position: "undereye" });
    if (flags.includes("tired")) concerns.push({ label: "Tired Aging Skin", position: "forehead" });
    if (flags.includes("undereye")) concerns.push({ label: "Under-Eye Concerns", position: "undereye" });
    if (flags.includes("repairNeed")) concerns.push({ label: "Repair Need", position: "cheeks" });
    
    // ============ INDIGO AURA CONCERNS (Deep Pigmentation) ============
    if (flags.includes("deepPigmentation")) concerns.push({ label: "Deep Pigmentation", position: "cheeks" });
    if (flags.includes("stubbornTone")) concerns.push({ label: "Stubborn Uneven Tone", position: "forehead" });
    if (flags.includes("undereyeDarkness")) concerns.push({ label: "Under-Eye Darkness", position: "undereye" });
    if (flags.includes("patchyTone")) concerns.push({ label: "Patchy Tone", position: "cheeks" });
    
    // ============ BLUE AURA CONCERNS (Tanning/Hydration) ============
    if (flags.includes("tanning")) concerns.push({ label: "Tanning / Sun Damage", position: "forehead" });
    if (flags.includes("diffuseUnevenTone")) concerns.push({ label: "Uneven Skin Tone", position: "cheeks" });
    if (flags.includes("dryness")) concerns.push({ label: "Dehydrated Skin", position: "cheeks" });
    if (flags.includes("pigmentation")) concerns.push({ label: "Mild Pigmentation", position: "cheeks" });
    
    // ============ YELLOW AURA CONCERNS (Glow/Brightness) ============
    if (flags.includes("dullness")) concerns.push({ label: "Dullness", position: "forehead" });
    if (flags.includes("lackOfGlow")) concerns.push({ label: "Lack of Glow", position: "nose" });
    if (flags.includes("tiredLooking")) concerns.push({ label: "Tired-Looking Skin", position: "undereye" });
    if (flags.includes("brightnessDeficit")) concerns.push({ label: "Brightness Deficit", position: "forehead" });
    
    // ============ ORANGE AURA CONCERNS (Texture/Congestion) ============
    if (flags.includes("blackheads")) concerns.push({ label: "Blackheads", position: "nose" });
    if (flags.includes("pores")) concerns.push({ label: "Clogged Pores", position: "nose" });
    if (flags.includes("texture")) concerns.push({ label: "Rough Texture", position: "cheeks" });
    if (flags.includes("acne")) concerns.push({ label: "Breakouts", position: "chin" });
    if (flags.includes("congestion")) concerns.push({ label: "Skin Congestion", position: "chin" });
    
    // ============ GREEN AURA CONCERNS (Calming/Balance) ============
    if (flags.includes("redness")) concerns.push({ label: "Redness / Irritation", position: "cheeks" });
    if (flags.includes("calmingNeed")) concerns.push({ label: "Needs Calming", position: "cheeks" });
    if (flags.includes("sensitivity")) concerns.push({ label: "Sensitivity", position: "cheeks" });
    if (flags.includes("poreReset")) concerns.push({ label: "Pore Reset Need", position: "nose" });
    
    // ============ PINK AURA CONCERNS (Lips/Softness) ============
    if (flags.includes("lipDarkness")) concerns.push({ label: "Dark Lips", position: "lips" });
    if (flags.includes("dryLips")) concerns.push({ label: "Dry / Chapped Lips", position: "lips" });
    if (flags.includes("softness")) concerns.push({ label: "Needs Nourishment", position: "cheeks" });
    if (flags.includes("lips")) concerns.push({ label: "Lip Concerns", position: "lips" });
    
    // ============ RED AURA CONCERNS (Barrier/Damage) ============
    if (flags.includes("barrier")) concerns.push({ label: "Barrier Damage", position: "jawline" });
    if (flags.includes("flaking")) concerns.push({ label: "Flaking / Peeling", position: "cheeks" });
    if (flags.includes("sensitivityDryness")) concerns.push({ label: "Sensitive Dry Skin", position: "jawline" });
    if (flags.includes("weakRoots")) concerns.push({ label: "Depleted Skin", position: "jawline" });
    
    // ============ HAIR CONCERNS (only if hair image was uploaded) ============
    // Don't show hair concerns for face-only Digital analysis
    const hasHairImage = form.imageHair !== null && form.imageHair !== undefined;
    if (hasHairImage) {
      if (flags.includes("frizz")) concerns.push({ label: "Dry / Frizzy Hair", position: "hair" });
      if (flags.includes("dullHair")) concerns.push({ label: "Dull Hair", position: "hair" });
      if (flags.includes("hairfall")) concerns.push({ label: "Hair Fall", position: "scalp" });
      if (flags.includes("oilyscalp")) concerns.push({ label: "Oily Scalp", position: "scalp" });
      if (flags.includes("scalpSensitivity")) concerns.push({ label: "Scalp Sensitivity", position: "scalp" });
    }
    
    return concerns;
  }, [form.detectedFlags]);

  // Build summary for doctor review
  const summary = useMemo(() => ({
    customerName: form.name || '',
    phone: form.phone || '',
    primaryAura: primaryMeta?.label || result?.primary,
    secondaryAura: secondaryMeta?.label || result?.secondary,
    topSkinConcerns: detectedConcerns.filter(c => !['scalp', 'hair'].includes(c.position)).map(c => c.label),
    topHairConcerns: detectedConcerns.filter(c => ['scalp', 'hair'].includes(c.position)).map(c => c.label),
  }), [form, result, primaryMeta, secondaryMeta, detectedConcerns]);

  // Handle Ask Doctor
  const handleAskDoctor = async () => {
    try {
      await axios.post(`${API}/doctor-review-request`, {
        customer_name: summary.customerName,
        phone: summary.phone,
        primary_aura: summary.primaryAura,
        top_skin_concerns: summary.topSkinConcerns,
        top_hair_concerns: summary.topHairConcerns,
        analysis_mode: "digital"
      });
      
      // Open WhatsApp
      const message = encodeURIComponent(
        `Hi Divine Aura! I just completed my Digital Aura Analysis.\n\n` +
        `My Primary Aura: ${summary.primaryAura}\n` +
        `Concerns: ${[...summary.topSkinConcerns, ...summary.topHairConcerns].join(", ")}\n\n` +
        `I'd like to speak with a dermatologist about my results.`
      );
      window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${message}`, '_blank');
    } catch (e) {
      console.error("Doctor request error:", e);
    }
  };

  // Handle no valid analysis case OR low confidence case
  if (result?.noValidAnalysis || result?.lowConfidence) {
    const isLowConfidence = result?.lowConfidence && !result?.noValidAnalysis;
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="max-w-xl mx-auto px-4 py-8 pb-32"
        data-testid="no-analysis-page"
      >
        <div className="text-center mb-8">
          <img src="/divine-aura-result-logo.png" alt="Divine Aura" className="w-28 h-28 mx-auto object-contain mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">
            {isLowConfidence ? "Analysis Needs More Clarity" : "Let's Try Again"}
          </h2>
          <p className="text-gray-600">
            {result.message || (isLowConfidence 
              ? "We could not confidently detect a strong dominant aura from this image. Please try another image with better lighting, or use Hybrid Analysis for more accurate results."
              : "We couldn't analyze your image. Please try again with a clearer photo."
            )}
          </p>
        </div>
        <div className="space-y-3">
          <button onClick={onReset} className="btn-divine w-full" data-testid="try-again-btn">
            <RotateCcw className="w-5 h-5 mr-2" /> Try Another Photo
          </button>
          {isLowConfidence && (
            <p className="text-center text-sm text-gray-500">
              For best results, ensure good lighting and face the camera directly
            </p>
          )}
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="max-w-xl mx-auto px-4 py-6 pb-32"
      data-testid="digital-results-page"
    >
      {/* DIVINE AURA LOGO AT TOP */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center mb-6"
      >
        <img 
          src="/divine-aura-result-logo.png" 
          alt="Divine Aura" 
          className="w-28 h-28 mx-auto object-contain mb-2"
        />
        <p className="text-sm text-gray-500">Your Aura Analysis Results</p>
      </motion.div>

      {/* A. IMAGE SECTION - Clean & Premium (No misleading hotspot markers) */}
      {form.capturedImageUrl && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
          data-testid="analyzed-image-section"
        >
          <div className="relative mx-auto max-w-xs">
            {/* Clean image with soft gradient border - no markers since location confidence is simulated */}
            <div 
              className="aspect-square rounded-2xl overflow-hidden shadow-lg p-1"
              style={{ 
                background: `linear-gradient(135deg, ${primaryMeta?.color || '#9B7ED9'}, ${secondaryMeta?.color || '#C84B8A'})` 
              }}
            >
              <div className="w-full h-full rounded-xl overflow-hidden">
                <img 
                  src={form.capturedImageUrl} 
                  alt="Your analyzed photo" 
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
          
          {/* B. DETECTED CONCERNS SUMMARY - Clean card below image */}
          {detectedConcerns.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              className="mt-4"
            >
              <div 
                className="rounded-xl p-4 border"
                style={{ 
                  background: `linear-gradient(135deg, ${primaryMeta?.color}08 0%, ${primaryMeta?.color}03 100%)`,
                  borderColor: `${primaryMeta?.color}20`
                }}
              >
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-3 text-center">
                  Areas We Noticed
                </p>
                
                {/* Concern pills - clean summary without fake location markers */}
                <div className="flex flex-wrap justify-center gap-2">
                  {detectedConcerns.slice(0, 5).map((concern, idx) => {
                    // Group concerns by zone for cleaner display
                    const zoneLabels = {
                      forehead: 'Upper Face',
                      eyes: 'Eye Area',
                      undereye: 'Under-Eye',
                      cheeks: 'Cheeks',
                      chin: 'Lower Face',
                      lips: 'Lips',
                      nose: 'T-Zone',
                      jawline: 'Jawline',
                      scalp: 'Scalp',
                      hair: 'Hair',
                      skin: 'Overall Skin'
                    };
                    const zone = zoneLabels[concern.position] || '';
                    
                    return (
                      <div
                        key={idx}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium shadow-sm"
                        style={{ 
                          backgroundColor: `${primaryMeta?.color}15`,
                          color: primaryMeta?.color || '#9B7ED9',
                          border: `1px solid ${primaryMeta?.color}30`
                        }}
                      >
                        <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: primaryMeta?.color }} />
                        <span>{concern.label}</span>
                        {zone && <span className="text-gray-400 text-[10px]">• {zone}</span>}
                      </div>
                    );
                  })}
                </div>
                
                <p className="text-center text-[10px] text-gray-400 mt-3">
                  Based on your image analysis • Use Hybrid mode for detailed mapping
                </p>
              </div>
            </motion.div>
          )}
        </motion.div>
      )}
      
      {/* Digital Analysis Disclaimer - Subtle note near results */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.08 }}
        className="mb-4 px-2"
      >
        <p className="text-[10px] text-gray-400 text-center leading-relaxed">
          Digital analysis is based on visible skin appearance in your photo. Results may vary with lighting, angle, and image quality.
        </p>
      </motion.div>
      
      {/* B. ASK THE DOCTOR BUTTON (Near Top) */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="mb-6"
      >
        <button
          onClick={handleAskDoctor}
          className="w-full flex items-center justify-center gap-3 py-3 px-4 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-xl font-medium hover:opacity-90 transition-opacity shadow-md"
          data-testid="ask-doctor-btn"
        >
          <Stethoscope className="w-5 h-5" />
          Ask the Doctor
        </button>
        <p className="text-center text-xs text-gray-500 mt-1">Get personalized guidance via WhatsApp</p>
      </motion.div>
      
      {/* C. PRIMARY AURA SECTION */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="mb-4"
        data-testid="primary-aura-section"
      >
        <div 
          className="rounded-2xl p-5 border-2"
          style={{ 
            background: `linear-gradient(135deg, ${primaryMeta?.color}15 0%, ${primaryMeta?.color}05 100%)`,
            borderColor: `${primaryMeta?.color}40`
          }}
        >
          <div className="flex items-center gap-3 mb-3">
            <div 
              className="w-12 h-12 rounded-full flex items-center justify-center"
              style={{ background: primaryMeta?.gradient }}
            >
              {primaryMeta?.icon && <primaryMeta.icon className="w-6 h-6 text-white" />}
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Primary Aura</p>
              <h3 className="text-xl font-bold" style={{ color: primaryMeta?.color }}>{primaryMeta?.label}</h3>
            </div>
          </div>
          
          <p className="text-sm text-gray-700 mb-3">{primaryCopy?.concernTitle}</p>
          
          {/* Short concern summary */}
          <div className="flex flex-wrap gap-1.5">
            {(primaryKit?.concerns || []).slice(0, 4).map((concern, idx) => (
              <span key={idx} className="px-2 py-0.5 bg-white/70 rounded-full text-xs text-gray-600">
                {concern}
              </span>
            ))}
          </div>
        </div>
      </motion.div>
      
      {/* D. SUPPORTING AURA SECTION - Only show if secondary aura is meaningful */}
      {result?.secondary && secondaryMeta && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mb-6"
          data-testid="secondary-aura-section"
        >
          <div 
            className="rounded-2xl p-4 border"
            style={{ 
              background: `linear-gradient(135deg, ${secondaryMeta?.color}10 0%, ${secondaryMeta?.color}03 100%)`,
              borderColor: `${secondaryMeta?.color}30`
            }}
          >
            <div className="flex items-center gap-3 mb-2">
              <div 
                className="w-10 h-10 rounded-full flex items-center justify-center"
                style={{ background: secondaryMeta?.gradient }}
              >
                {secondaryMeta?.icon && <secondaryMeta.icon className="w-5 h-5 text-white" />}
              </div>
              <div>
                <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">Supporting Aura</p>
                <h4 className="text-lg font-semibold" style={{ color: secondaryMeta?.color }}>{secondaryMeta?.label}</h4>
              </div>
            </div>
            
            <p className="text-sm text-gray-600">{secondaryCopy?.concernTitle}</p>
          </div>
        </motion.div>
      )}
      
      {/* E. YOUR CARE STRATEGY */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="divine-card mb-6"
        data-testid="care-strategy-section"
      >
        <h3 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
          <Activity className="w-5 h-5 text-purple-500" />
          Your Care Strategy
        </h3>
        <p className="text-sm text-gray-600 mb-3">{primaryKit?.strategicNotes}</p>
        <div className="flex flex-wrap gap-2">
          {(primaryMeta?.ritualFocus || []).map((focus, idx) => (
            <span key={idx} className="px-3 py-1 bg-purple-50 text-purple-700 rounded-full text-sm font-medium">
              {focus}
            </span>
          ))}
        </div>
      </motion.div>
      
      {/* F. YOUR SKIN RITUAL - Products from PRIMARY AURA ONLY */}
      {primaryKit?.skinProducts?.length > 0 ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="divine-card mb-6"
          data-testid="skin-ritual-section"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-pink-500" />
              Your Skin Ritual
            </h3>
            <span className="text-xs px-2 py-1 bg-pink-50 text-pink-600 rounded-full font-medium">
              {primaryKit?.kitName}
            </span>
          </div>
          
          {/* Note: Products based on Primary Aura */}
          <p className="text-xs text-gray-400 mb-3">Based on your {primaryMeta?.label || 'Primary Aura'}</p>
          
          <div className="space-y-2">
            {primaryKit.skinProducts.map((product, idx) => (
              <div key={idx} className="flex items-center gap-3 p-3 bg-gradient-to-r from-pink-50 to-purple-50 rounded-xl">
                <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center text-pink-500 font-semibold text-sm shadow-sm">
                  {idx + 1}
                </div>
                <span className="text-sm text-gray-700 font-medium">{product}</span>
              </div>
            ))}
          </div>
        </motion.div>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="divine-card mb-6"
          data-testid="skin-ritual-fallback"
        >
          <h3 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-pink-500" />
            Your Skin Ritual
          </h3>
          <p className="text-sm text-gray-500">
            Ritual mapping for this aura is not configured yet. Please contact us for personalized recommendations.
          </p>
        </motion.div>
      )}
      
      {/* G. YOUR HAIR RITUAL - Products from PRIMARY AURA ONLY (only if hair products exist in PRIMARY AURA mapping) */}
      {primaryKit?.hairProducts?.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="divine-card mb-6"
          data-testid="hair-ritual-section"
        >
          <h3 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
            <Wind className="w-5 h-5 text-emerald-500" />
            Your Hair Ritual
          </h3>
          
          {/* Note: Products based on Primary Aura */}
          <p className="text-xs text-gray-400 mb-3">Based on your {primaryMeta?.label || 'Primary Aura'}</p>
          
          <div className="space-y-2">
            {primaryKit.hairProducts.map((product, idx) => (
              <div key={idx} className="flex items-center gap-3 p-3 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl">
                <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center text-emerald-500 font-semibold text-sm shadow-sm">
                  {idx + 1}
                </div>
                <span className="text-sm text-gray-700 font-medium">{product}</span>
              </div>
            ))}
          </div>
        </motion.div>
      )}
      
      {/* Start Over Button */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.7 }}
        className="text-center"
      >
        <button
          onClick={onReset}
          className="text-sm text-gray-500 hover:text-gray-700 underline"
          data-testid="start-over-btn"
        >
          Start a New Analysis
        </button>
      </motion.div>
    </motion.div>
  );
};

// Results Page
const ResultsPage = ({ form, result, onReset, onAskDermat, onDownload }) => {
  const [customizeItem, setCustomizeItem] = useState(null);
  
  // These must be called unconditionally (React hooks rules)
  const primaryMeta = AURA_META[result?.primary];
  const secondaryMeta = AURA_META[result?.secondary];
  const routineStrategy = result?.noValidAnalysis ? [] : buildRoutineStrategy(result?.primary, result?.secondary, result?.energyState);
  const topPriorities = result?.noValidAnalysis ? [] : buildTopPriorities(form);
  const primaryCopy = AURA_COPY[result?.primary];
  const secondaryCopy = AURA_COPY[result?.secondary];
  
  // Build summary for doctor review and customize page
  const summary = useMemo(() => {
    if (result?.noValidAnalysis) return {};
    
    const skinConcerns = (form.skin_concerns || []).concat(form.skin_goals || []);
    const hairConcerns = (form.hair_concerns || []).concat(form.hair_feel_goals || []);
    
    return {
      submissionId: form.submissionId || null,
      customerName: form.name || '',
      phone: form.phone || '',
      email: form.email || '',
      analysisMode: form.analysisMode || '',
      primaryAura: primaryMeta?.label || result.primary,
      primaryMeaning: primaryCopy?.resultMeaning || primaryMeta?.summary || '',
      secondaryAura: secondaryMeta?.label || result.secondary,
      secondaryMeaning: secondaryCopy?.resultMeaning || secondaryMeta?.summary || '',
      energyState: ENERGY_STATE_META[result.energyState]?.label || result.energyState || '',
      topSkinConcerns: skinConcerns.length > 0 ? skinConcerns : topPriorities.filter(p => !p.toLowerCase().includes('hair')),
      topHairConcerns: hairConcerns.length > 0 ? hairConcerns : topPriorities.filter(p => p.toLowerCase().includes('hair')),
      careStrategy: routineStrategy,
    };
  }, [form, result, primaryMeta, secondaryMeta, primaryCopy, secondaryCopy, topPriorities, routineStrategy]);
  
  // Handle opening customize page
  const handleOpenCustomize = (item) => {
    setCustomizeItem(item);
  };
  
  // Handle case where Digital-only analysis couldn't produce valid results
  if (result?.noValidAnalysis) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="max-w-xl mx-auto px-4 md:px-6 py-8 pb-32 md:py-12 md:pb-12"
        data-testid="no-analysis-page"
      >
        <div className="text-center mb-8">
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", bounce: 0.4 }}
            className="mx-auto mb-6"
          >
            <img 
              src="/divine-aura-result-logo.png" 
              alt="Divine Aura" 
              className="w-28 h-28 mx-auto object-contain"
            />
          </motion.div>
          <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-4">
            Let&apos;s Try a Different Approach
          </h2>
        </div>
        
        <div className="divine-card mb-6">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-r from-amber-100 to-orange-100 flex items-center justify-center">
              <AlertCircle className="w-8 h-8 text-amber-600" />
            </div>
            <p className="text-gray-700 mb-4">
              {result.message || "Digital-only image analysis requires our AI processing which is coming soon."}
            </p>
            <p className="text-gray-600 text-sm mb-6">
              For the most accurate aura analysis right now, we recommend using our <strong>Questionnaire</strong> or <strong>Hybrid</strong> mode.
            </p>
          </div>
          
          <div className="space-y-3">
            <button
              onClick={() => {
                onReset();
              }}
              className="btn-divine w-full flex items-center justify-center gap-2"
              data-testid="try-questionnaire-btn"
            >
              <Sparkles className="w-5 h-5" />
              Try Questionnaire Mode
            </button>
            
            <p className="text-center text-sm text-gray-500">
              Takes just 2 minutes • Highly accurate results
            </p>
          </div>
        </div>
        
        <div className="text-center">
          <p className="text-xs text-gray-400">
            Our AI-powered image analysis feature is being enhanced for better accuracy.
          </p>
        </div>
      </motion.div>
    );
  }
  
  // If customize page is open, show it
  if (customizeItem) {
    return (
      <CustomizeForMePage
        item={customizeItem}
        summary={summary}
        onBack={() => setCustomizeItem(null)}
      />
    );
  }
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="max-w-4xl mx-auto px-4 md:px-6 py-8 pb-32 md:py-12 md:pb-12"
      data-testid="results-page"
    >
      {/* Premium Header with Logo */}
      <div className="text-center mb-12">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: "spring", bounce: 0.4 }}
          className="mx-auto mb-6"
        >
          <img 
            src="/divine-aura-result-logo.png" 
            alt="Divine Aura" 
            className="w-32 h-32 md:w-40 md:h-40 mx-auto object-contain"
            data-testid="result-logo"
          />
        </motion.div>
        <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold bg-gradient-to-r from-purple-600 via-pink-600 to-red-500 bg-clip-text text-transparent mb-4">
          Your Divine Aura Analysis
        </h1>
        <p className="text-gray-600 text-lg">
          {form.name ? `${form.name}, here's` : "Here's"} what your skin, hair, and scalp are asking for right now
        </p>
      </div>
      
      {/* Aura Result Meaning Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="divine-card mb-8"
        data-testid="aura-meaning-section"
      >
        <div className="mb-6">
          <h3 className="text-lg font-bold text-gray-800 mb-2 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full" style={{ background: primaryMeta?.color }} />
            Your Primary Aura = Main Concern Focus
          </h3>
          <div className="p-4 rounded-xl" style={{ background: `linear-gradient(135deg, ${primaryMeta?.color}15 0%, ${primaryMeta?.color}05 100%)` }}>
            <div className="flex items-center gap-3 mb-2">
              <div 
                className="w-10 h-10 rounded-full flex items-center justify-center"
                style={{ background: primaryMeta?.gradient }}
              >
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div>
                <h4 className="font-bold text-gray-800">{primaryMeta?.label}</h4>
                <p className="text-sm font-medium" style={{ color: primaryMeta?.color }}>{primaryCopy?.concernTitle || primaryMeta?.theme}</p>
              </div>
            </div>
            <p className="text-gray-600 text-sm">{primaryCopy?.resultMeaning || primaryMeta?.summary}</p>
          </div>
        </div>
        
        <div>
          <h3 className="text-lg font-bold text-gray-800 mb-2 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full" style={{ background: secondaryMeta?.color }} />
            Your Supporting Aura = Secondary Concern Focus
          </h3>
          <div className="p-4 rounded-xl" style={{ background: `linear-gradient(135deg, ${secondaryMeta?.color}15 0%, ${secondaryMeta?.color}05 100%)` }}>
            <div className="flex items-center gap-3 mb-2">
              <div 
                className="w-10 h-10 rounded-full flex items-center justify-center"
                style={{ background: secondaryMeta?.gradient }}
              >
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div>
                <h4 className="font-bold text-gray-800">{secondaryMeta?.label}</h4>
                <p className="text-sm font-medium" style={{ color: secondaryMeta?.color }}>{AURA_COPY[result.secondary]?.concernTitle || secondaryMeta?.theme}</p>
              </div>
            </div>
            <p className="text-gray-600 text-sm">{AURA_COPY[result.secondary]?.resultMeaning || secondaryMeta?.summary}</p>
          </div>
        </div>
      </motion.div>
      
      {/* Energy State */}
      <div className="mb-8">
        <EnergyStateCard state={result.energyState} />
      </div>
      
      {/* Ask Doctor Section with Top Concerns */}
      <AskDoctorSection summary={summary} />
      
      {/* Top Concerns Tags */}
      {topPriorities.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="result-card mb-8"
          data-testid="top-priorities"
        >
          <div className="flex flex-wrap gap-2 md:gap-3">
            {topPriorities.map((priority, i) => (
              <span key={i} className="px-4 py-2 bg-gradient-to-r from-purple-50 via-pink-50 to-red-50 text-pink-700 rounded-full text-sm font-medium border border-pink-100">
                {priority}
              </span>
            ))}
          </div>
        </motion.div>
      )}
      
      {/* Care Strategy */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="result-card mb-8"
        data-testid="care-strategy"
      >
        <h3 className="text-xl font-bold text-gray-800 mb-4">Your Care Strategy</h3>
        <div className="flex flex-wrap gap-2 md:gap-3">
          {routineStrategy.map((strategy, i) => (
            <span key={i} className="px-4 py-2 bg-gradient-to-r from-emerald-50 to-teal-50 text-emerald-700 rounded-full text-sm font-medium border border-emerald-100">
              {strategy}
            </span>
          ))}
        </div>
      </motion.div>
      
      {/* Skin Ritual with Product Cards */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="result-card mb-8"
        data-testid="skin-ritual"
      >
        <h3 className="text-xl font-bold text-gray-800 mb-4">Your Skin Ritual</h3>
        <div className="space-y-3">
          {(primaryMeta?.skinProducts || []).map((product, i) => {
            const isObject = typeof product === 'object';
            const productData = isObject ? product : { name: product, category: "customize" };
            return (
              <ProductRecommendationCard 
                key={i} 
                item={productData} 
                form={form} 
                result={result}
                onOpenCustomize={handleOpenCustomize}
              />
            );
          })}
        </div>
      </motion.div>
      
      {/* Hair Ritual with Product Cards */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="result-card mb-8"
        data-testid="hair-ritual"
      >
        <h3 className="text-xl font-bold text-gray-800 mb-4">Your Hair Ritual</h3>
        <div className="space-y-3">
          {(primaryMeta?.hairProducts || []).map((product, i) => {
            const isObject = typeof product === 'object';
            const productData = isObject ? product : { name: product, category: "customize" };
            return (
              <ProductRecommendationCard 
                key={i} 
                item={productData} 
                form={form} 
                result={result}
                onOpenCustomize={handleOpenCustomize}
              />
            );
          })}
        </div>
      </motion.div>
      
      {/* Lifestyle Guidance */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="result-card mb-8"
        data-testid="lifestyle-guidance"
      >
        <h3 className="text-xl font-bold text-gray-800 mb-4">Small Changes, Visible Difference</h3>
        <div className="grid md:grid-cols-2 gap-3 md:gap-4">
          {[
            "Stay hydrated - aim for 8 glasses of water daily",
            "Get 7-8 hours of quality sleep",
            "Protect skin from sun with SPF 30+ daily",
            "Include omega-3 rich foods in your diet",
            "Practice stress-reducing activities",
            "Avoid touching your face frequently"
          ].map((tip, i) => (
            <div key={i} className="flex items-start gap-3 p-3 bg-gradient-to-r from-amber-50 to-yellow-50 rounded-xl border border-amber-100">
              <Check className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <span className="text-gray-700 text-sm">{tip}</span>
            </div>
          ))}
        </div>
      </motion.div>
      
      {/* Start Over */}
      <div className="text-center mt-8">
        <button
          onClick={onReset}
          className="text-gray-500 hover:text-pink-600 underline transition-colors"
          data-testid="start-over-btn"
        >
          Start a New Analysis
        </button>
      </div>
    </motion.div>
  );
};

// ============ MAIN APP ============
function App() {
  const [screen, setScreen] = useState("hero");
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    age_band: "",
    analysisMode: "hybrid",
    buying_priorities: [],
    skin_concerns: [],
    skin_goals: [],
    hair_concerns: [],
    hair_feel_goals: [],
    routine_feeling: "",
    texture_preference: [],
    fragrance_preference: [],
    makeup_vibe: "",
    makeup_coverage: "",
    brand_disappointments: [],
    brand_solutions: [],
    body_skin: [],
    early_access: "",
    notes: "",
    agreePrivacy: false,
    askDermat: false,
    imageFace: null,
    imageHair: null,
    capturedImageUrl: null,
    detectedFlags: [],
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [validationError, setValidationError] = useState("");

  const result = useMemo(() => computeAuraScores(form), [form]);

  // Scroll to top when screen or step changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [screen, step]);

  // Questionnaire steps (only for questionnaire and hybrid modes)
  const questionSteps = [
    "buying_priorities",
    "skin_concerns",
    "skin_goals",
    "hair_concerns",
    "hair_feel_goals",
    "routine_feeling",
    "texture_preference",
    "fragrance_preference",
    "makeup_vibe",
    "makeup_coverage",
    "brand_disappointments",
    "brand_solutions",
    "body_skin",
    "early_access",
  ];

  // Calculate total steps based on mode
  const getTotalSteps = () => {
    if (form.analysisMode === "digital") {
      return 2; // Contact info + Upload/Review
    } else if (form.analysisMode === "questionnaire") {
      return questionSteps.length + 2; // Contact + Questions + Review (no upload)
    } else {
      // hybrid
      return questionSteps.length + 2; // Contact + Questions + Upload/Review
    }
  };
  
  const totalSteps = getTotalSteps();

  // Function to compute detected flags for digital analysis (returns flags, doesn't set state)
  // STRICTLY DETERMINISTIC: Same image always returns same flags - NO RANDOMNESS
  const computeDigitalFlags = useCallback(() => {
    const flags = [];
    
    // For Digital-only mode (no questionnaire), analyze image deterministically
    if (form.analysisMode === "digital") {
      // Check if user has uploaded any images
      const hasImages = form.imageFace || form.imageHair;
      
      if (!hasImages) {
        flags.push("no_images_uploaded");
        return flags;
      }
      
      // ============ DETERMINISTIC HASH FROM IMAGE METADATA ============
      // The same image file ALWAYS produces the exact same hash
      // Using only stable, immutable properties: name, size, lastModified
      const faceProps = form.imageFace 
        ? `${form.imageFace.name}_${form.imageFace.size}_${form.imageFace.lastModified}`
        : '';
      const hairProps = form.imageHair
        ? `${form.imageHair.name}_${form.imageHair.size}_${form.imageHair.lastModified}`
        : '';
      const imageString = faceProps + hairProps;
      
      // Create stable 32-bit hash - same input ALWAYS gives same output
      let hash = 5381; // DJB2 hash seed
      for (let i = 0; i < imageString.length; i++) {
        hash = ((hash << 5) + hash) + imageString.charCodeAt(i);
        hash = hash & 0xFFFFFFFF; // Keep as 32-bit
      }
      hash = Math.abs(hash);
      
      // ============ SINGLE DOMINANT CONCERN CATEGORY SELECTION ============
      // Instead of detecting multiple conflicting concerns, we select ONE primary category
      // Plus a SECONDARY category for dual-aura support
      // This ensures stable, consistent results for the same image
      
      if (form.imageFace) {
        // Divide hash space into 8 primary aura zones (roughly equal distribution)
        // Each zone maps to a specific aura's concern category
        const primaryZone = hash % 100; // 0-99 range for finer control
        
        // Secondary zone from different hash bits for Supporting Aura
        const secondaryZone = Math.floor(hash / 100) % 100;
        
        // Helper function to add flags for a zone
        const addFlagsForZone = (zone, isSecondary = false) => {
          const addFlag = (flag) => {
            if (!flags.includes(flag)) flags.push(flag);
          };
          
          if (zone < 8) {
            // RED ZONE (0-7): Barrier damage, flaking, sensitivity+dryness
            addFlag("barrier");
            if (!isSecondary) addFlag("sensitivityDryness");
            if ((hash >> 8) % 2 === 0 && !isSecondary) addFlag("flaking");
          } else if (zone < 22) {
            // ORANGE ZONE (8-21): Texture, clogged pores, blackheads, congestion
            addFlag("pores");
            addFlag("texture");
            if ((hash >> 8) % 3 === 0 && !isSecondary) addFlag("blackheads");
          } else if (zone < 35) {
            // INDIGO ZONE (22-34): Deep pigmentation, stubborn patches, under-eye darkness
            addFlag("deepPigmentation");
            if ((hash >> 8) % 2 === 0 && !isSecondary) addFlag("stubbornTone");
          } else if (zone < 50) {
            // PURPLE ZONE (35-49): Fine lines, wrinkles, tired aging, repair need
            addFlag("lines");
            if ((hash >> 8) % 2 === 0 && !isSecondary) addFlag("tired");
          } else if (zone < 68) {
            // BLUE ZONE (50-67): Tanning, diffuse uneven tone, dehydration
            addFlag("tanning");
            addFlag("dryness");
            if ((hash >> 8) % 2 === 0 && !isSecondary) addFlag("diffuseUnevenTone");
          } else if (zone < 85) {
            // YELLOW ZONE (68-84): Dullness, lack of glow, tired-looking
            addFlag("dullness");
            addFlag("lackOfGlow");
          } else if (zone < 94) {
            // GREEN ZONE (85-93): Mild redness, calming need, balance
            addFlag("redness");
            if ((hash >> 8) % 2 === 0 && !isSecondary) addFlag("calmingNeed");
          } else {
            // PINK ZONE (94-99): Lips, softness, feminine glow - CONSERVATIVE
            addFlag("lipDarkness");
            if (!isSecondary) addFlag("dryLips");
          }
        };
        
        // Add primary zone flags
        addFlagsForZone(primaryZone, false);
        
        // Add secondary zone flags for Supporting Aura (only if different zone category)
        // This enables dual-aura results
        const getZoneCategory = (z) => {
          if (z < 8) return 'red';
          if (z < 22) return 'orange';
          if (z < 35) return 'indigo';
          if (z < 50) return 'purple';
          if (z < 68) return 'blue';
          if (z < 85) return 'yellow';
          if (z < 94) return 'green';
          return 'pink';
        };
        
        const primaryCategory = getZoneCategory(primaryZone);
        const secondaryCategory = getZoneCategory(secondaryZone);
        
        // Only add secondary flags if it's a different category
        if (primaryCategory !== secondaryCategory) {
          addFlagsForZone(secondaryZone, true);
        }
      }
      
      // ============ HAIR ANALYSIS (Only if hair image provided) ============
      // Hair concerns are ADDITIVE and don't affect primary aura zone selection
      if (form.imageHair) {
        const hairHash = Math.abs(
          5381 + (form.imageHair.size || 1) * 33 + (form.imageHair.lastModified || 1)
        );
        const hairZone = hairHash % 5;
        
        if (hairZone === 0) flags.push("frizz");         // -> Blue
        else if (hairZone === 1) flags.push("dullHair"); // -> Blue
        else if (hairZone === 2) flags.push("hairfall"); // -> Red
        else if (hairZone === 3) flags.push("oilyscalp"); // -> Orange
        else flags.push("scalpSensitivity");             // -> Green
      }
      
      return [...new Set(flags)]; // Remove any duplicates
    } else if (form.analysisMode === "hybrid") {
      // For Hybrid mode - use questionnaire data to enhance digital analysis
      if (form.skin_concerns.includes("Dehydrated, dry or tight-feeling skin")) flags.push("dryness");
      if (form.skin_concerns.includes("Dull, tired-looking complexion")) flags.push("dullness");
      if (form.skin_concerns.includes("Sensitive or easily irritated skin")) flags.push("redness");
      if (form.skin_concerns.includes("Pigmentation or uneven skin tone")) flags.push("pigmentation");
      if (form.skin_concerns.includes("Acne, breakouts or excess oil")) flags.push("acne");
      if (form.skin_concerns.includes("Early fine lines, wrinkles or sagging")) flags.push("lines");
      if (form.hair_concerns.includes("Dry, frizzy or rough texture")) flags.push("frizz");
      if (form.hair_concerns.includes("Weak roots or breakage")) flags.push("hairfall");
      if (form.hair_concerns.includes("Oily scalp")) flags.push("oilyscalp");
      if (form.hair_concerns.includes("Sensitive or itchy scalp")) flags.push("scalpSensitivity");
    }
    
    return [...new Set(flags)];
  }, [form.analysisMode, form.imageFace, form.imageHair, form.skin_concerns, form.hair_concerns]);

  const handleSubmit = async () => {
    // Digital mode bypasses privacy checkbox (privacy note shown in capture step)
    // Questionnaire and Hybrid modes still require checkbox
    if (form.analysisMode !== "digital" && !form.agreePrivacy) return;
    setLoading(true);

    try {
      // Compute flags synchronously for digital/hybrid modes
      let detectedFlags = form.detectedFlags;
      if (form.analysisMode !== "questionnaire") {
        detectedFlags = computeDigitalFlags();
      }
      
      // Create updated form with detected flags for scoring
      const formWithFlags = { ...form, detectedFlags };
      const updatedResult = computeAuraScores(formWithFlags);
      
      // Update form state with detected flags
      setForm(prev => ({ ...prev, detectedFlags }));
      
      // Submit to backend
      await axios.post(`${API}/analyze/submit`, {
        user_info: {
          name: form.name,
          email: form.email,
          phone: form.phone,
          address: form.address,
          age_band: form.age_band,
        },
        questionnaire_data: {
          buying_priorities: form.buying_priorities,
          skin_concerns: form.skin_concerns,
          skin_goals: form.skin_goals,
          hair_concerns: form.hair_concerns,
          hair_feel_goals: form.hair_feel_goals,
          routine_feeling: form.routine_feeling,
          texture_preference: form.texture_preference,
          fragrance_preference: form.fragrance_preference,
          makeup_vibe: form.makeup_vibe,
          makeup_coverage: form.makeup_coverage,
          buying_matters: form.buying_priorities,
          brand_disappointments: form.brand_disappointments,
          brand_solutions: form.brand_solutions,
          early_access: form.early_access,
          body_skin: form.body_skin,
        },
        analysis_mode: form.analysisMode,
        detected_flags: detectedFlags,
        aura_result: {
          primary_aura: updatedResult.primary,
          secondary_aura: updatedResult.secondary,
          energy_state: updatedResult.energyState,
          scores: updatedResult.scores,
          percentages: updatedResult.percentages,
        },
        ask_dermat: form.askDermat,
        notes: form.notes,
      });

      setSubmitted(true);
      setScreen("results");
    } catch (error) {
      console.error("Submission error:", error);
      // Still show results even if API fails
      setSubmitted(true);
      setScreen("results");
    } finally {
      setLoading(false);
    }
  };

  const handleAskDermat = async () => {
    setForm((prev) => ({ ...prev, askDermat: true }));
    try {
      await axios.post(`${API}/dermat-review`, {
        submission_id: "pending",
        user_info: {
          name: form.name,
          email: form.email,
          phone: form.phone,
          address: form.address,
          age_band: form.age_band,
        },
        report_payload: {
          primary_aura: result.primary,
          secondary_aura: result.secondary,
          energy_state: result.energyState,
        },
      });
    } catch (error) {
      console.error("Dermat review error:", error);
    }
  };

  const handleDownload = () => {
    console.log("handleDownload called", { form, result });
    if (!result || !result.primary) {
      alert("No analysis results available. Please complete the analysis first.");
      return;
    }
    
    try {
      const primaryMeta = AURA_META[result.primary] || {};
      const secondaryMeta = AURA_META[result.secondary] || {};
      const energyMeta = ENERGY_STATE_META[result.energyState] || {};
      const topPriorities = buildTopPriorities(form) || [];
      const routineStrategy = buildRoutineStrategy(result.primary, result.secondary, result.energyState) || [];
      
      // Separate skin and hair concerns
      const skinConcerns = (form.skin_concerns || []).concat(form.skin_goals || []);
      const hairConcerns = (form.hair_concerns || []).concat(form.hair_feel_goals || []);
      
      // Build report data
      const reportData = {
        customerName: form.name || '',
        primaryConcernAura: primaryMeta.label || result.primary,
        primaryConcernMeaning: AURA_COPY[result.primary]?.resultMeaning || primaryMeta.summary || '',
        secondaryConcernAura: secondaryMeta.label || result.secondary,
        secondaryConcernMeaning: AURA_COPY[result.secondary]?.resultMeaning || secondaryMeta.summary || '',
        topSkinConcerns: skinConcerns.length > 0 ? skinConcerns : topPriorities.filter(p => !p.toLowerCase().includes('hair')),
        topHairConcerns: hairConcerns.length > 0 ? hairConcerns : topPriorities.filter(p => p.toLowerCase().includes('hair')),
        careStrategy: routineStrategy,
        skinRitual: (primaryMeta.skinProducts || []).map(p => typeof p === 'string' ? p : p.name),
        hairRitual: (primaryMeta.hairProducts || []).map(p => typeof p === 'string' ? p : p.name),
        energyState: energyMeta.label || result.energyState || '',
      };
      
      // Build report lines
      const lines = [
        "Divine Aura Analysis Report",
        "",
        reportData.customerName ? `Name: ${reportData.customerName}` : "",
        `Primary Concern Aura: ${reportData.primaryConcernAura}`,
        reportData.primaryConcernMeaning ? `Primary Concern Meaning: ${reportData.primaryConcernMeaning}` : "",
        reportData.secondaryConcernAura ? `Secondary Concern Aura: ${reportData.secondaryConcernAura}` : "",
        reportData.secondaryConcernMeaning ? `Secondary Concern Meaning: ${reportData.secondaryConcernMeaning}` : "",
        reportData.energyState ? `Energy State: ${reportData.energyState}` : "",
        reportData.topSkinConcerns?.length ? `Top Skin Concerns: ${reportData.topSkinConcerns.join(", ")}` : "",
        reportData.topHairConcerns?.length ? `Top Hair Concerns: ${reportData.topHairConcerns.join(", ")}` : "",
        reportData.careStrategy?.length ? `Care Strategy: ${reportData.careStrategy.join(", ")}` : "",
        reportData.skinRitual?.length ? `Skin Ritual: ${reportData.skinRitual.join(", ")}` : "",
        reportData.hairRitual?.length ? `Hair Ritual: ${reportData.hairRitual.join(", ")}` : "",
      ].filter(Boolean);
      
      // Create PDF using jsPDF
      const doc = new jsPDF();
      
      doc.setFont("helvetica", "bold");
      doc.setFontSize(18);
      doc.text("Divine Aura Analysis Report", 20, 20);
      
      doc.setFont("helvetica", "normal");
      doc.setFontSize(12);
      
      let y = 34;
      
      lines.slice(2).forEach((line) => {
        const wrapped = doc.splitTextToSize(line, 170);
        doc.text(wrapped, 20, y);
        y += wrapped.length * 7 + 3;
        
        if (y > 270) {
          doc.addPage();
          y = 20;
        }
      });
      
      // Save PDF - use data URI approach that works in iframes
      const filename = `Divine-Aura-Report-${form.name || 'Analysis'}.pdf`;
      const pdfDataUri = doc.output('datauristring');
      
      // Create a temporary link and click it
      const link = document.createElement('a');
      link.href = pdfDataUri;
      link.download = filename;
      link.style.display = 'none';
      document.body.appendChild(link);
      
      // Use setTimeout to ensure the link is in the DOM
      setTimeout(() => {
        link.click();
        document.body.removeChild(link);
      }, 100);
      
      console.log("PDF generated:", filename);
    } catch (error) {
      console.error("PDF error:", error);
      alert("Error generating PDF. Please try again.");
    }
  };

  const handleEmail = () => {
    console.log("handleEmail called", { form, result });
    if (!result || !result.primary) {
      alert("No analysis results available. Please complete the analysis first.");
      return;
    }
    
    try {
      const primaryMeta = AURA_META[result.primary] || {};
      const secondaryMeta = AURA_META[result.secondary] || {};
      const energyMeta = ENERGY_STATE_META[result.energyState] || {};
      const topPriorities = buildTopPriorities(form) || [];
      
      // Separate skin and hair concerns
      const skinConcerns = (form.skin_concerns || []).concat(form.skin_goals || []);
      const hairConcerns = (form.hair_concerns || []).concat(form.hair_feel_goals || []);
      
      // Build report text
      const lines = [
        "Divine Aura Analysis Report",
        "",
        form.name ? `Name: ${form.name}` : "",
        "",
        `Primary Concern Aura: ${primaryMeta.label || result.primary}`,
        AURA_COPY[result.primary]?.resultMeaning || primaryMeta.summary || '',
        "",
        `Secondary Concern Aura: ${secondaryMeta.label || result.secondary}`,
        AURA_COPY[result.secondary]?.resultMeaning || secondaryMeta.summary || '',
        "",
        energyMeta.label ? `Energy State: ${energyMeta.label}` : "",
        "",
        skinConcerns.length > 0 ? `Top Skin Concerns: ${skinConcerns.join(", ")}` : 
          (topPriorities.filter(p => !p.toLowerCase().includes('hair')).length > 0 ? 
            `Top Skin Concerns: ${topPriorities.filter(p => !p.toLowerCase().includes('hair')).join(", ")}` : ""),
        "",
        hairConcerns.length > 0 ? `Top Hair Concerns: ${hairConcerns.join(", ")}` :
          (topPriorities.filter(p => p.toLowerCase().includes('hair')).length > 0 ?
            `Top Hair Concerns: ${topPriorities.filter(p => p.toLowerCase().includes('hair')).join(", ")}` : ""),
        "",
        "---",
        "Discover your Divine Aura at divineaura.com"
      ].filter(Boolean);
      
      const reportText = lines.join("\n");
      
      // Use mailto directly - this works reliably
      const subject = encodeURIComponent("My Divine Aura Analysis Report");
      const body = encodeURIComponent(reportText);
      const mailtoUrl = `mailto:${form.email || ''}?subject=${subject}&body=${body}`;
      
      // Open mailto link
      window.location.href = mailtoUrl;
      
      console.log("Email mailto opened");
    } catch (error) {
      console.error("Email error:", error);
      alert("Error opening email. Please try again.");
    }
  };

  const handleReset = () => {
    setForm({
      name: "",
      email: "",
      phone: "",
      address: "",
      age_band: "",
      analysisMode: "hybrid",
      buying_priorities: [],
      skin_concerns: [],
      skin_goals: [],
      hair_concerns: [],
      hair_feel_goals: [],
      routine_feeling: "",
      texture_preference: [],
      fragrance_preference: [],
      makeup_vibe: "",
      makeup_coverage: "",
      brand_disappointments: [],
      brand_solutions: [],
      body_skin: [],
      early_access: "",
      notes: "",
      agreePrivacy: false,
      askDermat: false,
      imageFace: null,
      imageHair: null,
      capturedImageUrl: null,
      detectedFlags: [],
    });
    setStep(0);
    setSubmitted(false);
    setScreen("hero");
  };

  const handleNext = () => {
    // Clear any previous validation error
    setValidationError("");
    
    // Validate required fields on step 0 (contact info)
    if (step === 0 && (!form.name.trim() || !form.phone.trim())) {
      return; // Don't proceed if required fields are empty
    }
    
    // For questionnaire and hybrid modes, validate question answers
    if (form.analysisMode !== "digital" && step > 0) {
      const questionIndex = step - 1;
      if (questionIndex < questionSteps.length) {
        const questionKey = questionSteps[questionIndex];
        const question = QUESTIONS[questionKey];
        const answer = form[questionKey];
        const isMulti = question.max > 1;
        
        // Check if at least one option is selected
        if (isMulti) {
          // Multi-select: check if array has at least one item
          if (!answer || answer.length === 0) {
            setValidationError("Please select at least one option to proceed.");
            return;
          }
        } else {
          // Single-select: check if value is not empty
          if (!answer || answer === "") {
            setValidationError("Please select at least one option to proceed.");
            return;
          }
        }
      }
    }
    
    if (step < totalSteps - 1) {
      setStep(step + 1);
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (step > 0) {
      setStep(step - 1);
    } else if (screen === "questionnaire") {
      setScreen("mode");
    } else if (screen === "mode") {
      setScreen("intro");
    } else if (screen === "intro") {
      setScreen("hero");
    }
  };

  // Render based on screen
  if (screen === "hero") {
    return <HeroSection onStart={() => setScreen("intro")} />;
  }

  if (screen === "intro") {
    return <AuraIntroSection onContinue={() => setScreen("mode")} />;
  }

  if (screen === "mode") {
    return (
      <ModeSelection
        mode={form.analysisMode}
        setMode={(mode) => setForm({ ...form, analysisMode: mode })}
        onContinue={() => {
          setScreen("questionnaire");
          setStep(0);
        }}
      />
    );
  }

  if (screen === "results") {
    // Use DigitalResultsPage for Digital mode, regular ResultsPage for others
    if (form.analysisMode === "digital") {
      return (
        <DigitalResultsPage
          form={form}
          result={result}
          onReset={handleReset}
          onAskDermat={handleAskDermat}
        />
      );
    }
    
    return (
      <ResultsPage
        form={form}
        result={result}
        onReset={handleReset}
        onAskDermat={handleAskDermat}
        onDownload={handleDownload}
        onEmail={handleEmail}
      />
    );
  }

  // Questionnaire flow
  const renderQuestionnaireStep = () => {
    // Contact info step (first step for ALL modes)
    if (step === 0) {
      const canProceed = form.name.trim() && form.phone.trim();
      
      return (
        <motion.div
          key="contact"
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          className="divine-card"
          data-testid="contact-step"
        >
          <h3 className="text-2xl font-bold text-gray-800 mb-2">Let's Get to Know You</h3>
          <p className="text-gray-500 mb-6">Your information helps us personalize your experience</p>
          
          <div className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className={`input-divine ${!form.name.trim() && form.name !== '' ? 'border-red-300' : ''}`}
                placeholder="Your name"
                required
                data-testid="input-name"
              />
              {!form.name.trim() && form.name !== '' && (
                <p className="text-xs text-red-500 mt-1">Name is required</p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Phone <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className={`input-divine ${!form.phone.trim() && form.phone !== '' ? 'border-red-300' : ''}`}
                placeholder="+91 1234567890"
                required
                data-testid="input-phone"
              />
              {!form.phone.trim() && form.phone !== '' && (
                <p className="text-xs text-red-500 mt-1">Phone is required</p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email <span className="text-gray-400 text-xs font-normal">(Optional)</span>
              </label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="input-divine"
                placeholder="your@email.com"
                data-testid="input-email"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Address <span className="text-gray-400 text-xs font-normal">(Optional)</span>
              </label>
              <input
                type="text"
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                className="input-divine"
                placeholder="Your address"
                data-testid="input-address"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">Age Band</label>
              <div className="flex flex-wrap gap-2">
                {["18-24", "25-34", "35-44", "45-54", "55+"].map((age) => (
                  <button
                    key={age}
                    onClick={() => setForm({ ...form, age_band: age })}
                    className={`option-chip ${form.age_band === age ? "selected" : ""}`}
                    data-testid={`age-${age}`}
                  >
                    {age}
                  </button>
                ))}
              </div>
            </div>
          </div>
          
          {!canProceed && (
            <p className="text-sm text-gray-500 mt-4 flex items-center gap-2">
              <Shield className="w-4 h-4 text-gray-400" />
              Please fill in required fields to continue
            </p>
          )}
        </motion.div>
      );
    }

    // DIGITAL MODE: Step 1 is camera capture/upload (no questionnaire)
    if (form.analysisMode === "digital") {
      return (
        <motion.div
          key="digital-final"
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
        >
          <DigitalCaptureStep 
            form={form} 
            setForm={setForm} 
            loading={loading}
            onContinue={() => {
              // Directly trigger analysis - no separate privacy checkbox step
              handleSubmit();
            }}
          />
        </motion.div>
      );
    }

    // QUESTIONNAIRE & HYBRID MODES: Show question steps
    const questionIndex = step - 1;
    if (questionIndex < questionSteps.length) {
      const questionKey = questionSteps[questionIndex];
      const question = QUESTIONS[questionKey];
      const isMulti = question.max > 1;

      return (
        <motion.div
          key={questionKey}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          className="divine-card"
        >
          {isMulti ? (
            <OptionChipGroup
              title={question.title}
              subtitle={question.subtitle}
              options={question.options}
              selected={form[questionKey] || []}
              onChange={(val) => {
                setValidationError("");
                setForm({ ...form, [questionKey]: val });
              }}
              max={question.max}
            />
          ) : (
            <SingleSelectGroup
              title={question.title}
              subtitle={question.subtitle}
              options={question.options}
              selected={form[questionKey] || ""}
              onChange={(val) => {
                setValidationError("");
                setForm({ ...form, [questionKey]: val });
              }}
            />
          )}
          
          {/* Validation Error Message */}
          {validationError && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-4 p-3 bg-red-50 border border-red-200 rounded-xl flex items-center gap-2"
              data-testid="validation-error"
            >
              <Shield className="w-5 h-5 text-red-500 flex-shrink-0" />
              <p className="text-red-600 text-sm font-medium">{validationError}</p>
            </motion.div>
          )}
        </motion.div>
      );
    }

    // Final step - upload (hybrid only) and review
    return (
      <motion.div
        key="final"
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -20 }}
      >
        {form.analysisMode === "hybrid" && (
          <UploadZone form={form} setForm={setForm} />
        )}

        {form.analysisMode === "questionnaire" && (
          <div className="divine-card mb-6">
            <h3 className="text-xl font-semibold text-gray-800 mb-4">Additional Notes (Optional)</h3>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="input-divine min-h-[100px] resize-none"
              placeholder="Any specific concerns or information you'd like to share..."
              data-testid="notes-textarea"
            />
          </div>
        )}

        <div className="divine-card">
          <div className="flex items-start gap-3 mb-6">
            <button
              onClick={() => setForm({ ...form, agreePrivacy: !form.agreePrivacy })}
              className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                form.agreePrivacy ? "bg-gradient-to-r from-purple-500 via-pink-500 to-red-500 border-pink-500" : "border-gray-300"
              }`}
              data-testid="privacy-checkbox"
            >
              {form.agreePrivacy && <Check className="w-4 h-4 text-white" />}
            </button>
            <p className="text-sm text-gray-600">
              I agree to the privacy policy and understand that my data will be used to generate personalized recommendations.
              {form.analysisMode === "hybrid" && " Uploaded images are processed only for analysis and then deleted automatically after report generation."}
            </p>
          </div>

          {loading ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-r from-purple-400 via-pink-400 to-red-400 animate-pulse-soft" />
              <p className="text-gray-600">Generating your Divine Aura report...</p>
            </div>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={!form.agreePrivacy}
              className={`btn-divine w-full ${!form.agreePrivacy ? "opacity-50 cursor-not-allowed" : ""}`}
              data-testid="generate-report-btn"
            >
              <Sparkles className="inline-block w-5 h-5 mr-2" />
              Reveal My Divine Aura
            </button>
          )}
        </div>
      </motion.div>
    );
  };

  return (
    <div className="min-h-screen pb-28 md:pb-0" data-testid="questionnaire-screen">
      <ProgressHeader currentStep={step + 1} totalSteps={totalSteps} onBack={handleBack} />
      
      <main className="max-w-2xl mx-auto px-4 py-8 pb-32 md:pb-8">
        <AnimatePresence mode="wait">
          {renderQuestionnaireStep()}
        </AnimatePresence>
        
        {step < totalSteps - 1 && (
          <div className="mt-8 flex justify-end">
            <button onClick={handleNext} className="btn-divine" data-testid="next-btn">
              Continue
              <ChevronRight className="inline-block ml-2 w-5 h-5" />
            </button>
          </div>
        )}
      </main>
    </div>
  );
}

export default App;
